import { DBSchema, IDBPDatabase, deleteDB, openDB } from 'idb';
import { Appointment } from '../../interfaces/appointment/appointment';
import { User } from '../../interfaces/auth/user';
import { SalesCalendarEvent } from '../../interfaces/calendar/sales-calendar-data';
import { CustomerVisit } from '../../interfaces/customer-visit';
import { Customer } from '../../interfaces/customer/customer';
import { JobAddress } from '../../interfaces/job-address/job-address';
import { Lead } from '../../interfaces/lead/lead';
import { Note } from '../../interfaces/notes/note';
import { Statements } from '../../interfaces/offline/statements';
import { ProductSubCategory } from '../../interfaces/products/product-sub-category';
import { Proposal } from '../../interfaces/proposal/proposal';
import { Contract } from '../../interfaces/sales-management/contract';
import { Attachment } from '../../interfaces/attachments/attachment';
import { GeneratedContract } from '../../interfaces/contract/generated-contract';
import { SyncStatus } from '../../interfaces/offline/sync-status';
import { Customersearch } from '../../interfaces/customer/customersearch';
import { SoldSheet } from '../../interfaces/customer-visit/sold-sheet';
import { Production } from '../../interfaces/production/production';
import { Project } from '../../interfaces/production/project';
import { ProdAvailability } from '../../interfaces/production/prod-availability';
import { ProdAppointment } from '../../interfaces/production/prod-appointment';
import { ChangeOrder } from '../../interfaces/production/change-order';
import { Upsell } from '../../interfaces/production/upsell';
import { WrapUp } from '../../interfaces/wrap-up';
import { ServiceAvailability } from '../../interfaces/service/service-availability';
import { ServiceAppointment } from '../../interfaces/service/service-appointment';
import { ServiceVisit } from '../../interfaces/service/service-visit';
import { ServiceProject } from '../../interfaces/service/service-project';
import { GeoZoneFull } from '../../interfaces/zones/geo-zone-full';
import { AccountingInfo } from '../../interfaces/invoice/accounting-info';
import { Invoice } from '../../interfaces/production/invoice';
import { UpsellContract } from '../../interfaces/upsell/upsell-contract';
import { GeneratedUpsellContract } from '../../interfaces/upsell/generated-upsell-contract';
import { UpsellAppointment } from '../../interfaces/upsell/upsell-appointment';
import { UpsellCalendarEvent } from '../../interfaces/upsell/upsell-calendar-event';
import { UpsellVisit } from '../../interfaces/upsell/upsell-visit';
import { ChangeOrderContract } from '../../interfaces/production/change-order-contract';
import { GeneratedChangeOrderContract } from '../../interfaces/production/generated-change-order-contract';
import { CalendarNote } from '../../interfaces/calendar/calendar-note';
import { BillingStatement } from '../../interfaces/production/billing-statement';
import { FollowUp } from '../../interfaces/customer-visit/follow-up';
import { ContactMethod } from '../../interfaces/customer/contact-method';
import { NoBidReason } from '../../interfaces/customer-visit/no-bid-reason';
import { Disposition } from '../../interfaces/customer-visit/disposition';
import { SubDisposition } from '../../interfaces/customer-visit/sub-disposition';
import { PWAModel } from '../../interfaces/offline/pwa';
import { MailingRequest } from '../../interfaces/customer/mailing-requests';
import { Drawing } from '../../interfaces/drawing-program/drawing';
import { ProdDaily } from '../../interfaces/dailies/prod-daily';
import { TimeLog } from '../../interfaces/dailies/time-log';
import { SpectrumCrewMember } from '../../interfaces/dailies/spectrum-crew-member';
import { Crew } from '../../interfaces/dailies/crew';
import { SpectrumMaterial } from '../../interfaces/dailies/spectrum-material';
import { TimeBreakDown } from '../../interfaces/dailies/time-breakdown';
import { DailyMaterial } from '../../interfaces/dailies/daily-material';
import { DailyPaymentInfo } from '../../interfaces/dailies/daily-payment-info';
import { formatDate } from '@angular/common';
import { subDays } from 'date-fns';
import { DailyPrompt } from '../../interfaces/dailies/daily-prompt';
import { GeneratedInvoice } from '../../interfaces/production/generated-invoice';
import { ProductPricing } from '../../interfaces/proposal/product-pricing';
import { VehicleDocumentationAttachment } from '../../interfaces/attachments/vehicle-documentation-attachment';

interface MyDbSchema extends DBSchema {
  syncStatus: {
    key: number;
    value: {
      syncId: number;
      syncStatus: string;
      lastSynced: Date;
    };
    indexes: {
      syncId: number;
      syncStatus: string;
      lastSynced: Date;
    };
  };
  users: {
    key: string;
    value: User;
    indexes: {
      userId: string;
      userName: string;
      email: string;
      role: string;
    };
  };
  salesCalendar: {
    key: number;
    value: SalesCalendarEvent;
    indexes: {
      appointmentId: number;
      customerId: number;
      jobAddressId: number;
      jobaddressid: number;
      assignedTo: string;
    };
  };
  customers: {
    key: number;
    value: Customer;
    indexes: {
      customerid: number;
    };
  };
  jobAddresses: {
    key: number;
    value: JobAddress;
    indexes: {
      jobAddressId: number;
      customerId: number;
      isModified: number;
    };
  };
  appointments: {
    key: number;
    value: Appointment;
    indexes: {
      appointmentId: number;
      customerId: number;
      jobAddressId: number;
      date: string;
      effectiveScheduledDate: Date;
    };
  };
  leads: {
    key: number;
    value: Lead;
    indexes: {
      leadId: number;
      customerId: number;
      jobAddressId: number;
      leadType: number;
    };
  };
  customerVisits: {
    key: string;
    value: CustomerVisit;
    indexes: {
      customerVisitId: string;
      customerId: number;
      jobAddressId: number;
      appointmentId: number;
      createdDate: Date;
      status: number;
    };
  };
  proposals: {
    key: string;
    value: Proposal;
    indexes: {
      proposalId: string;
      customerId: number;
      jobAddressId: number;
      appointmentId: number;
      customerVisitId: string;
      proposalStatusName: string;
      status: number;
    };
  };
  products: {
    key: number;
    value: ProductSubCategory;
    indexes: {
      projectTypeId: number;
      projectName: string;
      subCategory: number;
      subCategoryName: string;
    };
  };
  statements: {
    key: number;
    value: Statements;
    indexes: {
      storeId: number;
      id: number;
      productId: number;
    };
  };
  contracts: {
    key: number;
    value: Contract;
    indexes: {
      contractId: number;
      proposalId: string;
      customerId: number;
      jobAddressId: number;
      appointmentId: number;
      customerVisitId: string;
    };
  };
  notes: {
    key: number;
    value: Note;
    indexes: {
      noteId: number;
      customerId: number;
      jobAddressId: number;
      functionId: number | string;
      functionName: string;
      // combined index functionId and functionName
      function: [number | string, string];
    };
  };
  attachments: {
    key: number;
    value: Attachment;
    indexes: {
      attachmentId: number;
      customerId: number;
      jobAddressId: number;
      functionId: string;
      // combined index attachmentId and jobAddressId
      attachmentId_jobAddressId: [number, number];
    };
  };
  generatedContracts: {
    key: number;
    value: GeneratedContract;
    indexes: {
      id: number;
      contractId: number;
      jobAddressId: number;
    };
  };
  soldSheets: {
    key: string;
    value: SoldSheet;
    indexes: {
      soldSheetId: string;
      contractId: number;
      proposalId: string;
      customerId: number;
      jobAddressId: number;
    };
  };
  prodAvailability: {
    key: number;
    value: ProdAvailability;
    indexes: {
      prodSchedId: number;
      customerId: number;
      jobAddressId: number;
      assignedToId: string;
    };
  };
  prodAppointments: {
    key: number;
    value: ProdAppointment;
    indexes: {
      prodSchedId: number;
      customerId: number;
      jobAddressId: number;
      assignedToId: string;
      contractId: number;
      scheduledDate: Date;
      effectiveScheduledDate: Date;
    };
  };
  projects: {
    key: number;
    value: Project;
    indexes: {
      projectId: number;
      customerId: number;
      jobAddressId: number;
      status: number;
      contractId: number;
    };
  };
  productionVisits: {
    key: string;
    value: Production;
    indexes: {
      productionId: string;
      customerId: number;
      jobAddressId: number;
      prodSchedId: number;
      projectId: number;
      contractId: number;
      status: number;
    };
  };
  changeOrders: {
    key: string;
    value: ChangeOrder;
    indexes: {
      changeOrderId: string;
      customerId: number;
      jobAddressId: number;
      functionId: string;
      status: number;
      projectid: number;
    };
  };
  upsells: {
    key: string;
    value: Upsell;
    indexes: {
      upsellId: string;
      customerId: number;
      jobAddressId: number;
      functionId: string;
      status: number;
      projectId: number;
    };
  };
  wrapUps: {
    key: string;
    value: WrapUp;
    indexes: {
      functionId: string;
      jobAddressId: number;
    };
  };
  serviceAvailability: {
    key: number;
    value: ServiceAvailability;
    indexes: {
      servAppointmentId: number;
      customerId: number;
      jobAddressId: number;
      assignedToId: string;
    };
  };
  serviceAppointments: {
    key: number;
    value: ServiceAppointment;
    indexes: {
      servAppointmentId: number;
      customerId: number;
      jobAddressId: number;
      assignedToId: string;
      scheduledDate: Date;
      effectiveScheduledDate: Date;
    };
  };
  serviceVisits: {
    key: string;
    value: ServiceVisit;
    indexes: {
      serviceVisitId: string;
      customerId: number;
      jobaddressId: number;
      serviceAppointmentId: number;
      serviceProjectId: number;
      status: number;
    };
  };
  serviceProjects: {
    key: number;
    value: ServiceProject;
    indexes: {
      serviceProjectId: number;
      customerId: number;
      jobAddressId: number;
      status: number;
    };
  };
  geoZonesFull: {
    key: number;
    value: GeoZoneFull;
    indexes: {
      id: number; // geoZoneId
      zoneId: number;
      name: string; // geoZoneName
      zoneName: string;
    };
  };
  accountingInfo: {
    key: number;
    value: AccountingInfo;
    indexes: {
      projectId: number;
      jobAddressId: number;
    };
  };
  invoices: {
    key: number;
    value: Invoice;
    indexes: {
      invoiceNumber: string;
      projectId: number;
      functionId: string;
      jobAddressId: number;
    };
  };
  upsellContracts: {
    key: number;
    value: UpsellContract;
    indexes: {
      upsellContractId: number;
      upsellId: string;
      customerId: number;
      jobAddressId: number;
      appointmentId: number;
      functionId: string;
    };
  };
  generatedUpsellContracts: {
    key: number;
    value: GeneratedUpsellContract;
    indexes: {
      id: number;
      upsellContractId: number;
      jobAddressId: number;
    };
  };
  upsellAppointments: {
    key: number;
    value: UpsellAppointment;
    indexes: {
      upsellAppointmentId: number;
      upsellId: string;
      customerId: number;
      jobAddressId: number;
      projectId: number;
    };
  };
  upsellCalendarEvents: {
    key: number;
    value: UpsellCalendarEvent;
    indexes: {
      upsellCalendarEventId: number;
      upsellId: string;
      jobAddressId: number;
      projectId: number;
      assignedToId: string;
    };
  };
  upsellVisits: {
    key: string;
    value: UpsellVisit;
    indexes: {
      upsellVisitId: string;
      customerId: number;
      jobAddressId: number;
      upsellAppointmentId: number;
      projectId: number;
      status: number;
    };
  };
  upsellProducts: {
    key: number;
    value: ProductSubCategory;
    indexes: {
      projectTypeId: number;
      projectName: string;
      subCategory: number;
      subCategoryName: string;
    };
  };
  changeOrderContracts: {
    key: number;
    value: ChangeOrderContract;
    indexes: {
      changeOrderContractId: number;
      changeOrderId: string;
      customerId: number;
      jobAddressId: number;
      appointmentId: number;
      functionId: string;
    };
  };
  generatedChangeOrderContracts: {
    key: number;
    value: GeneratedChangeOrderContract;
    indexes: {
      id: number;
      changeOrderContractId: number;
      jobAddressId: number;
    };
  };
  calendarNotes: {
    key: number;
    value: CalendarNote;
    indexes: {
      calendarNoteId: number;
      userId: string;
      noteType: string;
      noteDate: Date;
      isModified: number;
    };
  };
  billingStatements: {
    key: string;
    value: BillingStatement;
    indexes: {
      statementId: string;
      customerId: number;
      jobAddressId: number;
      functionId: string;
    };
  };
  followUps: {
    key: string;
    value: FollowUp;
    indexes: {
      followUpId: string;
      customerVisitId: string;
      customerId: number;
      jobAddressId: number;
      followUpDate: Date;
    };
  };
  contactMethods: {
    key: number;
    value: ContactMethod;
    indexes: {
      contactMethodId: number;
      customerId: number;
      jobAddressId: number;
    };
  };
  noBidReasons: {
    key: number;
    value: NoBidReason;
  };
  dispositions: {
    key: number;
    value: Disposition;
  };
  subDispositions: {
    key: number;
    value: SubDisposition;
  };
  mailingRequests: {
    key: number;
    value: MailingRequest;
    indexes: {
      mailingRequestId: number;
      customerId: number;
      jobAddressId: number;
      functionId: string;
      functionName: string;
    };
  };
  drawings: {
    key: string;
    value: Drawing;
    indexes: {
      drawingId: string;
      customerId: number;
      jobAddressId: number;
      functionId: string;
    };
  };
  prodDailies: {
    key: string;
    value: ProdDaily;
    indexes: {
      prodDailyId: string;
      customerId: number;
      jobAddressId: number;
      contractId: number;
      productionId: string;
      status: number;
      dailyDate: Date;
    };
  };
  timeLogs: {
    key: string;
    value: TimeLog;
    indexes: {
      timeLogId: string;
      customerId: number;
      jobAddressId: number;
      prodDailyId: string;
      productionId: string;
    };
  };
  timeBreakDowns: {
    key: string;
    value: TimeBreakDown;
    indexes: {
      timeBreakDownId: string;
      crewId: string;
      customerId: number;
      jobAddressId: number;
      prodDailyId: string;
      productionId: string;
    };
  };
  spectrumCrewMembers: {
    key: string;
    value: SpectrumCrewMember;
  };
  crews: {
    key: string;
    value: Crew;
    indexes: {
      crewId: string;
      prodDailyId: string;
      productionId: string;
      jobAddressId: number;
    };
  };
  spectrumMaterials: {
    key: number;
    value: SpectrumMaterial;
    indexes: {
      storeId: string;
    };
  };
  dailyMaterials: {
    key: string;
    value: DailyMaterial;
    indexes: {
      dailyMaterialId: string;
      prodDailyId: string;
      productionId: string;
      jobAddressId: number;
    };
  };
  dailyPaymentInfo: {
    key: string;
    value: DailyPaymentInfo;
    indexes: {
      dailyPaymentInfoId: string;
      customerId: number;
      jobAddressId: number;
      contractId: number;
      productionId: string;
    };
  };
  dailyPrompts: {
    key: string;
    value: DailyPrompt;
    indexes: {
      promptId: string;
      prodDailyId: string;
      jobAddressId: number;
      productionId: string;
    };
  };
  generatedInvoices: {
    key: string;
    value: GeneratedInvoice;
    indexes: {
      id: string;
      invoiceNumber: string;
      customerId: number;
      jobAddressId: number;
    };
  };
  productPricing: {
    key: number;
    value: ProductPricing;
    indexes: {
      productId: number;
      zoneId: number;
    };
  };
  vehicleDocumentationAttachments: {
    key: number;
    value: VehicleDocumentationAttachment;
  };
}

interface PWABackUpSchema extends DBSchema {
  syncStatus: {
    key: number;
    value: {
      syncId: number;
      syncStatus: string;
      lastSynced: Date;
    };
    indexes: {
      syncId: number;
      syncStatus: string;
      lastSynced: Date;
    };
  };
  pwaData: {
    key: number;
    value: PWAModel;
    indexes: {
      pwaBackUpId: number;
      createdDate: Date;
      jobAddressId: number;
      logged: number;
    };
  };
}

export class AccutraxIDB {
  db: IDBPDatabase<MyDbSchema>;
  pwaBackUpDb: IDBPDatabase<PWABackUpSchema>;

  async initIDB() {
    this.db = await openDB<MyDbSchema>('accutrax', 38, {
      blocked() {
        alert(
          `Please close any other tabs running Accutrax. Then refresh this page.`
        );
      },
      upgrade(db, oldVersion, newVersion, transaction) {
        if (oldVersion === 0) {
          console.log('v0');
          updateToLastestVersionFrom0();
        } else if (oldVersion < 23 && oldVersion > 0) {
          console.log('oldVersion', oldVersion);
          deleteDBAndReload();
        } else if (oldVersion === 23) {
          console.log('v23');
          // hypothetical future version
          console.log('upgradeToVersion24');
          upgradeToVersion24();
        } else if (oldVersion === 24) {
          console.log('upgradeToVersion25');
          upgradeToVersion25();
        } else if (oldVersion === 25) {
          console.log('upgradeToVersion26');
          upgradeToVersion26();
        } else if (oldVersion === 26) {
          console.log('upgradeToVersion27');
          upgradeToVersion27();
        } else if (oldVersion === 27) {
          console.log('upgradeToVersion28');
          upgradeToVersion28();
        } else if (oldVersion === 28) {
          console.log('upgradeToVersion29');
          upgradeToVersion29();
        } else if (oldVersion === 29) {
          console.log('upgradeToVersion30');
          upgradeToVersion30();
        } else if (oldVersion === 30) {
          console.log('upgradeToVersion31');
          upgradeToVersion31();
        } else if (oldVersion === 31) {
          console.log('upgradeToVersion32');
          upgradeToVersion32();
        } else if (oldVersion === 32) {
          console.log('upgradeToVersion33');
          upgradeToVersion33();
        } else if (oldVersion === 33) {
          console.log('upgradeToVersion34');
          upgradeToVersion34();
        } else if (oldVersion === 34) {
          console.log('upgradeToVersion35');
          upgradeToVersion35();
        } else if (oldVersion === 35) {
          console.log('upgradeToVersion36');
          upgradeToVersion36();
        } else if (oldVersion === 36) {
          console.log('upgradeToVersion37');
          upgradeToVersion37();
        } else if (oldVersion === 37) {
          console.log('upgradeToVersion38');
          upgradeToVersion38();
        }

        async function updateToLastestVersionFrom0() {
          console.log('updateToLastestVersionFrom0');
          const syncStatusStore = db.createObjectStore('syncStatus', {
            keyPath: 'syncId',
          });
          syncStatusStore.createIndex('syncId', 'syncId', { unique: true });
          syncStatusStore.createIndex('syncStatus', 'syncStatus', {
            unique: false,
          });
          syncStatusStore.createIndex('lastSynced', 'lastSynced', {
            unique: false,
          });

          const userStore = db.createObjectStore('users', {
            keyPath: 'userId',
          });
          userStore.createIndex('userId', 'userId', { unique: true });
          userStore.createIndex('userName', 'userName', { unique: true });
          userStore.createIndex('email', 'email', { unique: true });
          userStore.createIndex('role', 'role', { unique: true });

          const salesCalendarStore = db.createObjectStore('salesCalendar', {
            keyPath: 'appointmentId',
          });
          salesCalendarStore.createIndex('appointmentId', 'appointmentId', {
            unique: true,
          });
          salesCalendarStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          salesCalendarStore.createIndex('jobaddressid', 'jobaddressid', {
            unique: false,
          });
          salesCalendarStore.createIndex('assignedTo', 'assignedTo', {
            unique: false,
          });

          const customerStore = db.createObjectStore('customers', {
            keyPath: 'customerid',
          });
          customerStore.createIndex('customerid', 'customerid', {
            unique: true,
          });

          const jobAddressStore = db.createObjectStore('jobAddresses', {
            keyPath: 'jobAddressId',
          });
          jobAddressStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: true,
          });
          jobAddressStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          jobAddressStore.createIndex('isModified', 'isModified', {
            unique: false,
          });

          const appointmentStore = db.createObjectStore('appointments', {
            keyPath: 'appointmentId',
          });
          appointmentStore.createIndex('appointmentId', 'appointmentId', {
            unique: true,
          });
          appointmentStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          appointmentStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          appointmentStore.createIndex('date', 'date', {
            unique: false,
          });
          appointmentStore.createIndex(
            'effectiveScheduledDate',
            'effectiveScheduledDate',
            {
              unique: false,
            }
          );

          const leadStore = db.createObjectStore('leads', {
            keyPath: 'leadId',
          });
          leadStore.createIndex('leadId', 'leadId', {
            unique: true,
          });
          leadStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          leadStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          leadStore.createIndex('leadType', 'leadType', {
            unique: false,
          });

          const customerVisitStore = db.createObjectStore('customerVisits', {
            keyPath: 'customerVisitId',
          });
          customerVisitStore.createIndex('customerVisitId', 'customerVisitId', {
            unique: true,
          });
          customerVisitStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          customerVisitStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          customerVisitStore.createIndex('appointmentId', 'appointmentId', {
            unique: false,
          });
          customerVisitStore.createIndex('createdDate', 'createdDate', {
            unique: false,
          });
          customerVisitStore.createIndex('status', 'status', {
            unique: false,
          });

          const proposalStore = db.createObjectStore('proposals', {
            keyPath: 'proposalId',
          });
          proposalStore.createIndex('proposalId', 'proposalId', {
            unique: true,
          });
          proposalStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          proposalStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          proposalStore.createIndex('appointmentId', 'appointmentId', {
            unique: false,
          });
          proposalStore.createIndex('customerVisitId', 'customerVisitId', {
            unique: false,
          });
          proposalStore.createIndex('status', 'status', {
            unique: false,
          });

          const productStore = db.createObjectStore('products', {
            keyPath: 'id',
            autoIncrement: true,
          });
          productStore.createIndex('projectTypeId', 'projectTypeId', {
            unique: false,
          });
          productStore.createIndex('projectName', 'projectName', {
            unique: false,
          });
          productStore.createIndex('subCategory', 'subCategory', {
            unique: false,
          });
          productStore.createIndex('subCategoryName', 'subCategoryName', {
            unique: false,
          });

          const statementStore = db.createObjectStore('statements', {
            keyPath: 'storeId',
            autoIncrement: true,
          });
          statementStore.createIndex('storeId', 'storeId', {
            unique: true,
          });
          statementStore.createIndex('id', 'id', {
            unique: false,
          });
          statementStore.createIndex('productId', 'productId', {
            unique: false,
          });

          const contractStore = db.createObjectStore('contracts', {
            keyPath: 'contractId',
          });
          contractStore.createIndex('contractId', 'contractId', {
            unique: true,
          });
          contractStore.createIndex('proposalId', 'proposalId', {
            unique: false,
          });
          contractStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          contractStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          contractStore.createIndex('appointmentId', 'appointmentId', {
            unique: false,
          });
          contractStore.createIndex('customerVisitId', 'customerVisitId', {
            unique: false,
          });

          const noteStore = db.createObjectStore('notes', {
            keyPath: 'noteId',
          });
          noteStore.createIndex('noteId', 'noteId', {
            unique: true,
          });
          noteStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          noteStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          noteStore.createIndex('functionId', 'functionId', {
            unique: false,
          });
          noteStore.createIndex('functionName', 'functionName', {
            unique: false,
          });
          noteStore.createIndex('function', ['functionId', 'functionName'], {
            unique: false,
          });

          const attachmentStore = db.createObjectStore('attachments', {
            keyPath: 'attachmentId',
          });
          attachmentStore.createIndex('attachmentId', 'attachmentId', {
            unique: true,
          });
          attachmentStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          attachmentStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          attachmentStore.createIndex('functionId', 'functionId', {
            unique: false,
          });

          const generatedContractStore = db.createObjectStore(
            'generatedContracts',
            {
              keyPath: 'id',
            }
          );
          generatedContractStore.createIndex('id', 'id', {
            unique: true,
          });
          generatedContractStore.createIndex('contractId', 'contractId', {
            unique: false,
          });
          generatedContractStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });

          const soldSheetStore = db.createObjectStore('soldSheets', {
            keyPath: 'soldSheetId',
          });
          soldSheetStore.createIndex('soldSheetId', 'soldSheetId', {
            unique: true,
          });
          soldSheetStore.createIndex('proposalId', 'proposalId', {
            unique: false,
          });
          soldSheetStore.createIndex('contractId', 'contractId', {
            unique: false,
          });
          soldSheetStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          soldSheetStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });

          const prodAvailabilityStore = db.createObjectStore(
            'prodAvailability',
            {
              keyPath: 'prodSchedId',
            }
          );
          prodAvailabilityStore.createIndex('prodSchedId', 'prodSchedId', {
            unique: true,
          });
          prodAvailabilityStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          prodAvailabilityStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          prodAvailabilityStore.createIndex('assignedToId', 'assignedToId', {
            unique: false,
          });

          const prodAppointmentStore = db.createObjectStore(
            'prodAppointments',
            {
              keyPath: 'prodSchedId',
            }
          );
          prodAppointmentStore.createIndex('prodSchedId', 'prodSchedId', {
            unique: true,
          });
          prodAppointmentStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          prodAppointmentStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          prodAppointmentStore.createIndex('assignedToId', 'assignedToId', {
            unique: false,
          });
          prodAppointmentStore.createIndex('contractId', 'contractId', {
            unique: false,
          });
          prodAppointmentStore.createIndex('scheduledDate', 'scheduledDate', {
            unique: false,
          });
          prodAppointmentStore.createIndex(
            'effectiveScheduledDate',
            'effectiveScheduledDate',
            {
              unique: false,
            }
          );

          const projectStore = db.createObjectStore('projects', {
            keyPath: 'projectId',
          });
          projectStore.createIndex('projectId', 'projectId', {
            unique: true,
          });
          projectStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          projectStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          projectStore.createIndex('status', 'status', {
            unique: false,
          });
          projectStore.createIndex('contractId', 'contractId', {
            unique: false,
          });

          const productionVisitStore = db.createObjectStore(
            'productionVisits',
            {
              keyPath: 'productionId',
            }
          );
          productionVisitStore.createIndex('productionId', 'productionId', {
            unique: true,
          });
          productionVisitStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          productionVisitStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          productionVisitStore.createIndex('prodSchedId', 'prodSchedId', {
            unique: false,
          });
          productionVisitStore.createIndex('projectId', 'projectId', {
            unique: false,
          });
          productionVisitStore.createIndex('contractId', 'contractId', {
            unique: false,
          });
          productionVisitStore.createIndex('status', 'status', {
            unique: false,
          });

          const changeOrderStore = db.createObjectStore('changeOrders', {
            keyPath: 'changeOrderId',
          });

          changeOrderStore.createIndex('changeOrderId', 'changeOrderId', {
            unique: true,
          });
          changeOrderStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          changeOrderStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          changeOrderStore.createIndex('functionId', 'functionId', {
            unique: false,
          });
          changeOrderStore.createIndex('status', 'status', {
            unique: false,
          });
          changeOrderStore.createIndex('projectid', 'projectid', {
            unique: false,
          });

          const upsellStore = db.createObjectStore('upsells', {
            keyPath: 'upsellId',
          });

          upsellStore.createIndex('upsellId', 'upsellId', {
            unique: true,
          });
          upsellStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          upsellStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          upsellStore.createIndex('functionId', 'functionId', {
            unique: false,
          });
          upsellStore.createIndex('status', 'status', {
            unique: false,
          });
          upsellStore.createIndex('projectId', 'projectId', {
            unique: false,
          });

          const wrapUpStore = db.createObjectStore('wrapUps', {
            keyPath: 'functionId',
          });
          wrapUpStore.createIndex('functionId', 'functionId', {
            unique: true,
          });
          wrapUpStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });

          const serviceAvailabilityStore = db.createObjectStore(
            'serviceAvailability',
            {
              keyPath: 'servAppointmentId',
            }
          );
          serviceAvailabilityStore.createIndex(
            'servAppointmentId',
            'servAppointmentId',
            {
              unique: true,
            }
          );
          serviceAvailabilityStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          serviceAvailabilityStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          serviceAvailabilityStore.createIndex('assignedToId', 'assignedToId', {
            unique: false,
          });

          const serviceAppointmentsStore = db.createObjectStore(
            'serviceAppointments',
            {
              keyPath: 'servAppointmentId',
            }
          );
          serviceAppointmentsStore.createIndex(
            'servAppointmentId',
            'servAppointmentId',
            {
              unique: true,
            }
          );
          serviceAppointmentsStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          serviceAppointmentsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          serviceAppointmentsStore.createIndex('assignedToId', 'assignedToId', {
            unique: false,
          });
          serviceAppointmentsStore.createIndex(
            'scheduledDate',
            'scheduledDate',
            {
              unique: false,
            }
          );
          serviceAppointmentsStore.createIndex(
            'effectiveScheduledDate',
            'effectiveScheduledDate',
            {
              unique: false,
            }
          );

          const serviceVisitsStore = db.createObjectStore('serviceVisits', {
            keyPath: 'serviceVisitId',
          });
          serviceVisitsStore.createIndex('serviceVisitId', 'serviceVisitId', {
            unique: true,
          });
          serviceVisitsStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          serviceVisitsStore.createIndex('jobaddressId', 'jobaddressId', {
            unique: false,
          });
          serviceVisitsStore.createIndex(
            'serviceAppointmentId',
            'serviceAppointmentId',
            {
              unique: false,
            }
          );
          serviceVisitsStore.createIndex(
            'serviceProjectId',
            'serviceProjectId',
            {
              unique: false,
            }
          );
          serviceVisitsStore.createIndex('status', 'status', {
            unique: false,
          });

          const serviceProjectsStore = db.createObjectStore('serviceProjects', {
            keyPath: 'serviceProjectId',
          });
          serviceProjectsStore.createIndex(
            'serviceProjectId',
            'serviceProjectId',
            {
              unique: true,
            }
          );
          serviceProjectsStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          serviceProjectsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          serviceProjectsStore.createIndex('status', 'status', {
            unique: false,
          });

          const geoZonesFullStore = db.createObjectStore('geoZonesFull', {
            keyPath: 'id',
          });

          geoZonesFullStore.createIndex('id', 'id', {
            unique: true,
          });
          geoZonesFullStore.createIndex('name', 'name', {
            unique: true,
          });
          geoZonesFullStore.createIndex('zoneId', 'zoneId', {
            unique: false,
          });
          geoZonesFullStore.createIndex('zoneName', 'zoneName', {
            unique: false,
          });

          const accountingInfoStore = db.createObjectStore('accountingInfo', {
            keyPath: 'projectId',
          });

          accountingInfoStore.createIndex('projectId', 'projectId', {
            unique: true,
          });
          accountingInfoStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });

          const invoicesStore = db.createObjectStore('invoices', {
            keyPath: 'projectId',
          });

          invoicesStore.createIndex('projectId', 'projectId', {
            unique: true,
          });
          invoicesStore.createIndex('functionId', 'functionId', {
            unique: false,
          });
          invoicesStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });

          const upsellContractsStore = db.createObjectStore('upsellContracts', {
            keyPath: 'upsellContractId',
          });

          upsellContractsStore.createIndex(
            'upsellContractId',
            'upsellContractId',
            {
              unique: true,
            }
          );
          upsellContractsStore.createIndex('upsellId', 'upsellId', {
            unique: true,
          });
          upsellContractsStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          upsellContractsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          upsellContractsStore.createIndex('appointmentId', 'appointmentId', {
            unique: false,
          });
          upsellContractsStore.createIndex('functionId', 'functionId', {
            unique: false,
          });

          const generatedUpsellContracts = db.createObjectStore(
            'generatedUpsellContracts',
            {
              keyPath: 'id',
            }
          );

          generatedUpsellContracts.createIndex('id', 'id', {
            unique: true,
          });
          generatedUpsellContracts.createIndex(
            'upsellContractId',
            'upsellContractId',
            {
              unique: true,
            }
          );
          generatedUpsellContracts.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });

          const upsellAppointmentStore = db.createObjectStore(
            'upsellAppointments',
            {
              keyPath: 'upsellAppointmentId',
            }
          );

          upsellAppointmentStore.createIndex(
            'upsellAppointmentId',
            'upsellAppointmentId',
            {
              unique: true,
            }
          );
          upsellAppointmentStore.createIndex('upsellId', 'upsellId', {
            unique: true,
          });
          upsellAppointmentStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          upsellAppointmentStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          upsellAppointmentStore.createIndex('projectId', 'projectId', {
            unique: false,
          });

          const upsellCalendarEventsStore = db.createObjectStore(
            'upsellCalendarEvents',
            {
              keyPath: 'upsellCalendarEventId',
            }
          );

          upsellCalendarEventsStore.createIndex(
            'upsellCalendarEventId',
            'upsellCalendarEventId',
            {
              unique: true,
            }
          );
          upsellCalendarEventsStore.createIndex('upsellId', 'upsellId', {
            unique: true,
          });
          upsellCalendarEventsStore.createIndex(
            'jobAddressId',
            'jobAddressId',
            {
              unique: false,
            }
          );
          upsellCalendarEventsStore.createIndex('projectId', 'projectId', {
            unique: false,
          });
          upsellCalendarEventsStore.createIndex(
            'assignedToId',
            'assignedToId',
            {
              unique: false,
            }
          );

          const upsellVisitsStore = db.createObjectStore('upsellVisits', {
            keyPath: 'upsellVisitId',
          });

          upsellVisitsStore.createIndex('upsellVisitId', 'upsellVisitId', {
            unique: true,
          });
          upsellVisitsStore.createIndex(
            'upsellAppointmentId',
            'upsellAppointmentId',
            {
              unique: true,
            }
          );
          upsellVisitsStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          upsellVisitsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          upsellVisitsStore.createIndex('projectId', 'projectId', {
            unique: false,
          });
          upsellVisitsStore.createIndex('status', 'status', {
            unique: false,
          });

          const upsellProductsStore = db.createObjectStore('upsellProducts', {
            keyPath: 'id',
            autoIncrement: true,
          });
          upsellProductsStore.createIndex('projectTypeId', 'projectTypeId', {
            unique: false,
          });
          upsellProductsStore.createIndex('projectName', 'projectName', {
            unique: false,
          });
          upsellProductsStore.createIndex('subCategory', 'subCategory', {
            unique: false,
          });
          upsellProductsStore.createIndex(
            'subCategoryName',
            'subCategoryName',
            {
              unique: false,
            }
          );

          const changeOrderContractsStore = db.createObjectStore(
            'changeOrderContracts',
            {
              keyPath: 'changeOrderContractId',
            }
          );

          changeOrderContractsStore.createIndex(
            'changeOrderContractId',
            'changeOrderContractId',
            {
              unique: true,
            }
          );
          changeOrderContractsStore.createIndex(
            'changeOrderId',
            'changeOrderId',
            {
              unique: true,
            }
          );
          changeOrderContractsStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          changeOrderContractsStore.createIndex(
            'jobAddressId',
            'jobAddressId',
            {
              unique: false,
            }
          );
          changeOrderContractsStore.createIndex(
            'appointmentId',
            'appointmentId',
            {
              unique: false,
            }
          );
          changeOrderContractsStore.createIndex('functionId', 'functionId', {
            unique: false,
          });

          const generatedChangeOrderContractsStore = db.createObjectStore(
            'generatedChangeOrderContracts',
            {
              keyPath: 'id',
            }
          );

          generatedChangeOrderContractsStore.createIndex('id', 'id', {
            unique: true,
          });
          generatedChangeOrderContractsStore.createIndex(
            'changeOrderContractId',
            'changeOrderContractId',
            {
              unique: true,
            }
          );
          generatedChangeOrderContractsStore.createIndex(
            'jobAddressId',
            'jobAddressId',
            {
              unique: false,
            }
          );

          const calendarNotesStore = db.createObjectStore('calendarNotes', {
            keyPath: 'calendarNoteId',
          });

          calendarNotesStore.createIndex('calendarNoteId', 'calendarNoteId', {
            unique: true,
          });
          calendarNotesStore.createIndex('userId', 'userId', {
            unique: false,
          });
          calendarNotesStore.createIndex('noteType', 'noteType', {
            unique: false,
          });
          calendarNotesStore.createIndex('noteDate', 'noteDate', {
            unique: false,
          });
          calendarNotesStore.createIndex('isModified', 'isModified', {
            unique: false,
          });

          const billingStatementsStore = db.createObjectStore(
            'billingStatements',
            {
              keyPath: 'statementId',
            }
          );

          billingStatementsStore.createIndex('statementId', 'statementId', {
            unique: true,
          });
          billingStatementsStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          billingStatementsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          billingStatementsStore.createIndex('functionId', 'functionId', {
            unique: false,
          });

          const followUpsStore = db.createObjectStore('followUps', {
            keyPath: 'followUpId',
          });

          followUpsStore.createIndex('followUpId', 'followUpId', {
            unique: true,
          });
          followUpsStore.createIndex('customerVisitId', 'customerVisitId', {
            unique: false,
          });
          followUpsStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          followUpsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          followUpsStore.createIndex('followUpDate', 'followUpDate', {
            unique: false,
          });

          const contactMethodsStore = db.createObjectStore('contactMethods', {
            keyPath: 'contactMethodId',
          });

          contactMethodsStore.createIndex(
            'contactMethodId',
            'contactMethodId',
            {
              unique: true,
            }
          );
          contactMethodsStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          contactMethodsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });

          const noBidReasonsStore = db.createObjectStore('noBidReasons', {
            keyPath: 'noBidId',
          });

          const dispositionsStore = db.createObjectStore('dispositions', {
            keyPath: 'dispositionId',
          });

          const subDispositionsStore = db.createObjectStore('subDispositions', {
            keyPath: 'subDispositionId',
          });

          const mailingRequestsStore = db.createObjectStore('mailingRequests', {
            keyPath: 'mailingRequestId',
          });

          mailingRequestsStore.createIndex(
            'mailingRequestId',
            'mailingRequestId',
            {
              unique: true,
            }
          );
          mailingRequestsStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          mailingRequestsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          mailingRequestsStore.createIndex('functionId', 'functionId', {
            unique: false,
          });
          mailingRequestsStore.createIndex('functionName', 'functionName', {
            unique: false,
          });

          const drawingsStore = db.createObjectStore('drawings', {
            keyPath: 'drawingId',
          });

          drawingsStore.createIndex('drawingId', 'drawingId', {
            unique: true,
          });
          drawingsStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          drawingsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          drawingsStore.createIndex('functionId', 'functionId', {
            unique: false,
          });

          const prodDailiesStore = db.createObjectStore('prodDailies', {
            keyPath: 'prodDailyId',
          });

          prodDailiesStore.createIndex('prodDailyId', 'prodDailyId', {
            unique: true,
          });
          prodDailiesStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          prodDailiesStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          prodDailiesStore.createIndex('contractId', 'contractId', {
            unique: false,
          });
          prodDailiesStore.createIndex('productionId', 'productionId', {
            unique: false,
          });
          prodDailiesStore.createIndex('status', 'status', {
            unique: false,
          });
          prodDailiesStore.createIndex('dailyDate', 'dailyDate', {
            unique: false,
          });

          const timeLogsStore = db.createObjectStore('timeLogs', {
            keyPath: 'timeLogId',
          });

          timeLogsStore.createIndex('timeLogId', 'timeLogId', {
            unique: true,
          });
          timeLogsStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          timeLogsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          timeLogsStore.createIndex('prodDailyId', 'prodDailyId', {
            unique: false,
          });
          timeLogsStore.createIndex('productionId', 'productionId', {
            unique: false,
          });

          const timeBreakDownsStore = db.createObjectStore('timeBreakDowns', {
            keyPath: 'timeBreakDownId',
          });

          timeBreakDownsStore.createIndex(
            'timeBreakDownId',
            'timeBreakDownId',
            {
              unique: true,
            }
          );

          timeBreakDownsStore.createIndex('crewId', 'crewId', {
            unique: false,
          });

          timeBreakDownsStore.createIndex('customerId', 'customerId', {
            unique: false,
          });

          timeBreakDownsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });

          timeBreakDownsStore.createIndex('prodDailyId', 'prodDailyId', {
            unique: false,
          });

          timeBreakDownsStore.createIndex('productionId', 'productionId', {
            unique: false,
          });

          const spectrumCrewMembersStore = db.createObjectStore(
            'spectrumCrewMembers',
            {
              keyPath: 'employeeCode',
            }
          );

          const crewsStore = db.createObjectStore('crews', {
            keyPath: 'crewId',
          });

          crewsStore.createIndex('crewId', 'crewId', {
            unique: true,
          });
          crewsStore.createIndex('prodDailyId', 'prodDailyId', {
            unique: false,
          });
          crewsStore.createIndex('productionId', 'productionId', {
            unique: false,
          });
          crewsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });

          const spectrumMaterialsStore = db.createObjectStore(
            'spectrumMaterials',
            {
              keyPath: 'storeId',
              autoIncrement: true,
            }
          );

          const dailyMaterialsStore = db.createObjectStore('dailyMaterials', {
            keyPath: 'dailyMaterialId',
          });

          dailyMaterialsStore.createIndex(
            'dailyMaterialId',
            'dailyMaterialId',
            {
              unique: true,
            }
          );

          dailyMaterialsStore.createIndex('prodDailyId', 'prodDailyId', {
            unique: false,
          });

          dailyMaterialsStore.createIndex('productionId', 'productionId', {
            unique: false,
          });
          dailyMaterialsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });

          const dailyPaymentInfoStore = db.createObjectStore(
            'dailyPaymentInfo',
            {
              keyPath: 'dailyPaymentInfoId',
            }
          );

          dailyPaymentInfoStore.createIndex(
            'dailyPaymentInfoId',
            'dailyPaymentInfoId',
            {
              unique: true,
            }
          );

          dailyPaymentInfoStore.createIndex('customerId', 'customerId', {
            unique: false,
          });

          dailyPaymentInfoStore.createIndex('contractId', 'contractId', {
            unique: false,
          });

          dailyPaymentInfoStore.createIndex('productionId', 'productionId', {
            unique: false,
          });

          dailyPaymentInfoStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });

          const dailyPromptStore = db.createObjectStore('dailyPrompts', {
            keyPath: 'promptId',
          });

          dailyPromptStore.createIndex('promptId', 'promptId', {
            unique: true,
          });

          dailyPromptStore.createIndex('prodDailyId', 'prodDailyId', {
            unique: false,
          });

          dailyPromptStore.createIndex('productionId', 'productionId', {
            unique: false,
          });

          dailyPromptStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });

          const generatedInvoicesStore = db.createObjectStore(
            'generatedInvoices',
            {
              keyPath: 'id',
            }
          );

          generatedInvoicesStore.createIndex('id', 'id', {
            unique: true,
          });

          generatedInvoicesStore.createIndex('invoiceNumber', 'invoiceNumber', {
            unique: true,
          });

          generatedInvoicesStore.createIndex('customerId', 'customerId', {
            unique: false,
          });

          generatedInvoicesStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });

          const productPricingStore = db.createObjectStore('productPricing', {
            keyPath: 'id',
            autoIncrement: true,
          });

          productPricingStore.createIndex('productId', 'productId', {
            unique: false,
          });
          productPricingStore.createIndex('zoneId', 'zoneId', {
            unique: false,
          });

          const vehicleDocumentationAttachmentStore = db.createObjectStore(
            'vehicleDocumentationAttachments',
            {
              keyPath: 'documentId',
            }
          );
          // This is the end of updateToLastestVersionFrom0
        }

        function upgradeToVersion24() {
          const jobAddressStore = transaction.objectStore('jobAddresses');
          jobAddressStore.createIndex('isModified', 'isModified', {
            unique: false,
          });
        }

        function upgradeToVersion25() {
          const calendarNotesStore = db.createObjectStore('calendarNotes', {
            keyPath: 'calendarNoteId',
          });

          calendarNotesStore.createIndex('calendarNoteId', 'calendarNoteId', {
            unique: true,
          });
          calendarNotesStore.createIndex('userId', 'userId', {
            unique: false,
          });
          calendarNotesStore.createIndex('noteType', 'noteType', {
            unique: false,
          });
          calendarNotesStore.createIndex('noteDate', 'noteDate', {
            unique: false,
          });
          calendarNotesStore.createIndex('isModified', 'isModified', {
            unique: false,
          });
        }

        function upgradeToVersion26() {
          const billingStatementsStore = db.createObjectStore(
            'billingStatements',
            {
              keyPath: 'statementId',
            }
          );

          billingStatementsStore.createIndex('statementId', 'statementId', {
            unique: true,
          });
          billingStatementsStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          billingStatementsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
        }

        function upgradeToVersion27() {
          const followUpsStore = db.createObjectStore('followUps', {
            keyPath: 'followUpId',
          });

          followUpsStore.createIndex('followUpId', 'followUpId', {
            unique: true,
          });
          followUpsStore.createIndex('customerVisitId', 'customerVisitId', {
            unique: false,
          });
          followUpsStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          followUpsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          followUpsStore.createIndex('followUpDate', 'followUpDate', {
            unique: false,
          });
        }

        function upgradeToVersion28() {
          const customerVisitStore = transaction.objectStore('customerVisits');
          customerVisitStore.createIndex('createdDate', 'createdDate', {
            unique: false,
          });

          const changeOrderContractStore = transaction.objectStore(
            'changeOrderContracts'
          );
          changeOrderContractStore.deleteIndex('customerId');
          changeOrderContractStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          changeOrderContractStore.deleteIndex('jobAddressId');
          changeOrderContractStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          changeOrderContractStore.deleteIndex('appointmentId');
          changeOrderContractStore.createIndex(
            'appointmentId',
            'appointmentId',
            {
              unique: false,
            }
          );
          changeOrderContractStore.deleteIndex('functionId');
          changeOrderContractStore.createIndex('functionId', 'functionId', {
            unique: false,
          });

          const generatedChangeOrderContractStore = transaction.objectStore(
            'generatedChangeOrderContracts'
          );
          generatedChangeOrderContractStore.deleteIndex('jobAddressId');
          generatedChangeOrderContractStore.createIndex(
            'jobAddressId',
            'jobAddressId',
            {
              unique: false,
            }
          );

          const contactMethodsStore = db.createObjectStore('contactMethods', {
            keyPath: 'contactMethodId',
          });

          contactMethodsStore.createIndex(
            'contactMethodId',
            'contactMethodId',
            {
              unique: true,
            }
          );
          contactMethodsStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          contactMethodsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
        }

        function upgradeToVersion29() {
          const noBidReasonsStore = db.createObjectStore('noBidReasons', {
            keyPath: 'noBidId',
          });

          const dispositionsStore = db.createObjectStore('dispositions', {
            keyPath: 'dispositionId',
          });

          const subDispositionsStore = db.createObjectStore('subDispositions', {
            keyPath: 'subDispositionId',
          });

          const appointmentStore = transaction.objectStore('appointments');
          appointmentStore.createIndex('date', 'date', {
            unique: false,
          });
        }

        function upgradeToVersion30() {
          // When you do version 30 add this index and uncomment it above. Don't do the unique appointmnetId index
          const customerVisitStore = transaction.objectStore('customerVisits');
          customerVisitStore.createIndex('status', 'status', {
            unique: false,
          });
        }

        function upgradeToVersion31() {
          const serviceAppointmentsStore = transaction.objectStore(
            'serviceAppointments'
          );
          serviceAppointmentsStore.createIndex(
            'scheduledDate',
            'scheduledDate',
            {
              unique: false,
            }
          );

          const upsellContractsStore =
            transaction.objectStore('upsellContracts');

          upsellContractsStore.deleteIndex('customerId');
          upsellContractsStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          upsellContractsStore.deleteIndex('jobAddressId');
          upsellContractsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          upsellContractsStore.deleteIndex('appointmentId');
          upsellContractsStore.createIndex('appointmentId', 'appointmentId', {
            unique: false,
          });
          upsellContractsStore.deleteIndex('functionId');
          upsellContractsStore.createIndex('functionId', 'functionId', {
            unique: false,
          });

          const generatedUpsellContractsStore = transaction.objectStore(
            'generatedUpsellContracts'
          );

          generatedUpsellContractsStore.deleteIndex('jobAddressId');
          generatedUpsellContractsStore.createIndex(
            'jobAddressId',
            'jobAddressId',
            {
              unique: false,
            }
          );

          const upsellAppointmentStore =
            transaction.objectStore('upsellAppointments');

          upsellAppointmentStore.deleteIndex('customerId');
          upsellAppointmentStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          upsellAppointmentStore.deleteIndex('jobAddressId');
          upsellAppointmentStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          upsellAppointmentStore.deleteIndex('projectId');
          upsellAppointmentStore.createIndex('projectId', 'projectId', {
            unique: false,
          });

          const upsellCalendarEventsStore = transaction.objectStore(
            'upsellCalendarEvents'
          );

          upsellCalendarEventsStore.deleteIndex('jobAddressId');
          upsellCalendarEventsStore.createIndex(
            'jobAddressId',
            'jobAddressId',
            {
              unique: false,
            }
          );
          upsellCalendarEventsStore.deleteIndex('projectId');
          upsellCalendarEventsStore.createIndex('projectId', 'projectId', {
            unique: false,
          });

          const upsellVisitsStore = transaction.objectStore('upsellVisits');

          upsellVisitsStore.deleteIndex('customerId');
          upsellVisitsStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          upsellVisitsStore.deleteIndex('jobAddressId');
          upsellVisitsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          upsellVisitsStore.deleteIndex('projectId');
          upsellVisitsStore.createIndex('projectId', 'projectId', {
            unique: false,
          });
          upsellVisitsStore.deleteIndex('status');
          upsellVisitsStore.createIndex('status', 'status', {
            unique: false,
          });
        }

        function upgradeToVersion32() {
          const mailingRequestsStore = db.createObjectStore('mailingRequests', {
            keyPath: 'mailingRequestId',
          });

          mailingRequestsStore.createIndex(
            'mailingRequestId',
            'mailingRequestId',
            {
              unique: true,
            }
          );
          mailingRequestsStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          mailingRequestsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          mailingRequestsStore.createIndex('functionId', 'functionId', {
            unique: false,
          });
          mailingRequestsStore.createIndex('functionName', 'functionName', {
            unique: false,
          });
        }

        function upgradeToVersion33() {
          const drawingsStore = db.createObjectStore('drawings', {
            keyPath: 'drawingId',
          });

          drawingsStore.createIndex('drawingId', 'drawingId', {
            unique: true,
          });
          drawingsStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          drawingsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          drawingsStore.createIndex('functionId', 'functionId', {
            unique: false,
          });
        }

        function upgradeToVersion34() {
          const projectStore = transaction.objectStore('projects');

          projectStore.createIndex('contractId', 'contractId', {
            unique: false,
          });

          const prodDailiesStore = db.createObjectStore('prodDailies', {
            keyPath: 'prodDailyId',
          });

          prodDailiesStore.createIndex('prodDailyId', 'prodDailyId', {
            unique: true,
          });
          prodDailiesStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          prodDailiesStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          prodDailiesStore.createIndex('contractId', 'contractId', {
            unique: false,
          });
          prodDailiesStore.createIndex('productionId', 'productionId', {
            unique: false,
          });
          prodDailiesStore.createIndex('status', 'status', {
            unique: false,
          });
          prodDailiesStore.createIndex('dailyDate', 'dailyDate', {
            unique: false,
          });

          const productionVisitStore =
            transaction.objectStore('productionVisits');

          productionVisitStore.createIndex('contractId', 'contractId', {
            unique: false,
          });

          productionVisitStore.deleteIndex('prodSchedId');
          productionVisitStore.createIndex('prodSchedId', 'prodSchedId', {
            unique: false,
          });

          const timeLogsStore = db.createObjectStore('timeLogs', {
            keyPath: 'timeLogId',
          });

          timeLogsStore.createIndex('timeLogId', 'timeLogId', {
            unique: true,
          });
          timeLogsStore.createIndex('customerId', 'customerId', {
            unique: false,
          });
          timeLogsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          timeLogsStore.createIndex('prodDailyId', 'prodDailyId', {
            unique: false,
          });
          timeLogsStore.createIndex('productionId', 'productionId', {
            unique: false,
          });

          const timeBreakDownsStore = db.createObjectStore('timeBreakDowns', {
            keyPath: 'timeBreakDownId',
          });

          timeBreakDownsStore.createIndex(
            'timeBreakDownId',
            'timeBreakDownId',
            {
              unique: true,
            }
          );

          timeBreakDownsStore.createIndex('crewId', 'crewId', {
            unique: false,
          });

          timeBreakDownsStore.createIndex('customerId', 'customerId', {
            unique: false,
          });

          timeBreakDownsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });

          timeBreakDownsStore.createIndex('prodDailyId', 'prodDailyId', {
            unique: false,
          });

          timeBreakDownsStore.createIndex('productionId', 'productionId', {
            unique: false,
          });

          const spectrumCrewMembersStore = db.createObjectStore(
            'spectrumCrewMembers',
            {
              keyPath: 'employeeCode',
            }
          );

          const crewsStore = db.createObjectStore('crews', {
            keyPath: 'crewId',
          });

          crewsStore.createIndex('crewId', 'crewId', {
            unique: true,
          });
          crewsStore.createIndex('prodDailyId', 'prodDailyId', {
            unique: false,
          });
          crewsStore.createIndex('productionId', 'productionId', {
            unique: false,
          });
          crewsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });

          const spectrumMaterialsStore = db.createObjectStore(
            'spectrumMaterials',
            {
              keyPath: 'storeId',
              autoIncrement: true,
            }
          );

          const dailyMaterialsStore = db.createObjectStore('dailyMaterials', {
            keyPath: 'dailyMaterialId',
          });

          dailyMaterialsStore.createIndex(
            'dailyMaterialId',
            'dailyMaterialId',
            {
              unique: true,
            }
          );

          dailyMaterialsStore.createIndex('prodDailyId', 'prodDailyId', {
            unique: false,
          });

          dailyMaterialsStore.createIndex('productionId', 'productionId', {
            unique: false,
          });
          dailyMaterialsStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });

          const prodAppointmentStore =
            transaction.objectStore('prodAppointments');
          prodAppointmentStore.createIndex('contractId', 'contractId', {
            unique: false,
          });
          prodAppointmentStore.createIndex('scheduledDate', 'scheduledDate', {
            unique: false,
          });

          const dailyPaymentInfoStore = db.createObjectStore(
            'dailyPaymentInfo',
            {
              keyPath: 'dailyPaymentInfoId',
            }
          );

          dailyPaymentInfoStore.createIndex(
            'dailyPaymentInfoId',
            'dailyPaymentInfoId',
            {
              unique: true,
            }
          );

          dailyPaymentInfoStore.createIndex('customerId', 'customerId', {
            unique: false,
          });

          dailyPaymentInfoStore.createIndex('contractId', 'contractId', {
            unique: false,
          });

          dailyPaymentInfoStore.createIndex('productionId', 'productionId', {
            unique: false,
          });

          dailyPaymentInfoStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });

          const dailyPromptStore = db.createObjectStore('dailyPrompts', {
            keyPath: 'promptId',
          });

          dailyPromptStore.createIndex('promptId', 'promptId', {
            unique: true,
          });

          dailyPromptStore.createIndex('prodDailyId', 'prodDailyId', {
            unique: false,
          });

          dailyPromptStore.createIndex('productionId', 'productionId', {
            unique: false,
          });

          dailyPromptStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });

          const invoicesStore = transaction.objectStore('invoices');

          invoicesStore.createIndex('functionId', 'functionId', {
            unique: false,
          });

          const billingStatementsStore =
            transaction.objectStore('billingStatements');

          billingStatementsStore.createIndex('functionId', 'functionId', {
            unique: false,
          });

          const generatedInvoicesStore = db.createObjectStore(
            'generatedInvoices',
            {
              keyPath: 'id',
            }
          );

          generatedInvoicesStore.createIndex('id', 'id', {
            unique: true,
          });

          generatedInvoicesStore.createIndex('invoiceNumber', 'invoiceNumber', {
            unique: true,
          });

          generatedInvoicesStore.createIndex('customerId', 'customerId', {
            unique: false,
          });

          generatedInvoicesStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
        }

        function upgradeToVersion35() {
          const productPricingStore = db.createObjectStore('productPricing', {
            keyPath: 'id',
            autoIncrement: true,
          });

          productPricingStore.createIndex('productId', 'productId', {
            unique: false,
          });
          productPricingStore.createIndex('zoneId', 'zoneId', {
            unique: false,
          });
        }

        function upgradeToVersion36() {
          console.log('upgradeToVersion36 - Rebuild Customers Store');
          if (oldVersion < 36) {
            // Get the old customer store
            const oldCustomerStore = transaction.objectStore('customers');
            console.log('Old Customer Store:', oldCustomerStore);

            // Get all of the old customers
            oldCustomerStore.getAll().then((customers) => {
              console.log('Old Customers:', customers);

              // Delete the old store
              db.deleteObjectStore('customers');
              console.log('Old customer store deleted');

              // Create the new customer store and index
              const newCustomerStore = db.createObjectStore('customers', {
                keyPath: 'customerid',
              });
              newCustomerStore.createIndex('customerid', 'customerid', {
                unique: true,
              });
              console.log('New Customer Store:', newCustomerStore);

              // Insert them into the new store
              customers.forEach((customer) => {
                newCustomerStore.add(customer);
              });
              console.log('All customers added to new store');
            });
          }
        }

        function upgradeToVersion37() {
          const appointmentsStore = transaction.objectStore('appointments');
          appointmentsStore.createIndex(
            'effectiveScheduledDate',
            'effectiveScheduledDate',
            {
              unique: false,
            }
          );

          const prodAppointmentsStore =
            transaction.objectStore('prodAppointments');
          prodAppointmentsStore.createIndex(
            'effectiveScheduledDate',
            'effectiveScheduledDate',
            {
              unique: false,
            }
          );

          const serviceAppointmentsStore = transaction.objectStore(
            'serviceAppointments'
          );
          serviceAppointmentsStore.createIndex(
            'effectiveScheduledDate',
            'effectiveScheduledDate',
            {
              unique: false,
            }
          );
        }

        function upgradeToVersion38() {
          const vehicleDocumentationAttachmentStore = db.createObjectStore(
            'vehicleDocumentationAttachments',
            {
              keyPath: 'documentId',
            }
          );
        }

        function deleteDBAndReload() {
          // delete the database
          indexedDB.deleteDatabase('accutrax');
          location.reload();
        }
      },
      blocking() {
        // alert(`Accutrax is outdated, please close this tab.`);
        window.location.reload();
      },
      terminated() {
        alert(
          `Your database needs to be refreshed. If you have an issue contact the IT department.`
        );
        location.reload();
      },
    });

    this.pwaBackUpDb = await openDB<PWABackUpSchema>('pwaBackUp', 1, {
      blocked() {
        console.log('pwaBackUpDB blocked');
      },
      upgrade(pwaBackUpDb, oldVersion, newVersion, transaction) {
        console.log('PWA Back Up upgrade', oldVersion, newVersion, transaction);
        if (oldVersion === 0) {
          pwaBackUpUpdateToLatestVersionFrom0();
        }

        async function pwaBackUpUpdateToLatestVersionFrom0() {
          console.log('PWA Back Up updateToLastestVersionFrom0');
          const pwaDataStore = pwaBackUpDb.createObjectStore('pwaData', {
            keyPath: 'pwaBackUpId',
            autoIncrement: true,
          });

          pwaDataStore.createIndex('pwaBackUpId', 'pwaBackUpId', {
            unique: true,
          });
          pwaDataStore.createIndex('createdDate', 'createdDate', {
            unique: false,
          });
          pwaDataStore.createIndex('jobAddressId', 'jobAddressId', {
            unique: false,
          });
          pwaDataStore.createIndex('logged', 'logged', {
            unique: false,
          });
        }
      },
      blocking() {
        console.log('pwaBackUpDB blocking');
        window.location.reload();
      },
      terminated() {
        console.log('pwaBackUpDB terminated');
        location.reload();
      },
    });

    await this.initSyncStatus();
  }

  async deleteIndexedDB() {
    try {
      // this.db.close();
      await deleteDB('accutrax');
      console.log('IndexedDB database deleted successfully');
    } catch (error) {
      console.error('Error deleting IndexedDB database:', error);
    }
  }

  // PWA Back Up
  // Used mid-sync to store pending JSON uploads
  // Used as a back up if the uploads fails.
  async addPendingPWAData(pwaData: PWAModel) {
    await this.pwaBackUpDb.put('pwaData', pwaData);
  }

  async removePendingPWAData(pwaBackUpId: number) {
    await this.pwaBackUpDb.delete('pwaData', pwaBackUpId);
  }

  async getPendingPWADataByJobAddressId(jobAddressId: number) {
    return await this.pwaBackUpDb.getFromIndex(
      'pwaData',
      'jobAddressId',
      jobAddressId
    );
  }

  async getAllPendingPWAData(): Promise<PWAModel[]> {
    return await this.pwaBackUpDb.getAll('pwaData');
  }

  async clearPendingPWAData() {
    await this.pwaBackUpDb.clear('pwaData');
  }

  // Old PWA BackUp logic that isn't really used anymore.
  async addPWABackUpData(pwaData: PWAModel) {
    console.log('addPWABackUpData');
    pwaData.logged = 0;
    await this.pwaBackUpDb.add('pwaData', pwaData);
  }

  async getAllPWABackUpData(): Promise<PWAModel[]> {
    return await this.pwaBackUpDb.getAll('pwaData');
  }

  async getAllPWABackUpNeedingLogged(): Promise<PWAModel[]> {
    return await this.pwaBackUpDb.getAllFromIndex('pwaData', 'logged', 0);
  }

  async markPWABackUpAsLogged(pwaBackUpId: number) {
    const pwaData = await this.pwaBackUpDb.get('pwaData', pwaBackUpId);
    if (pwaData) {
      pwaData.logged = 1;
      await this.pwaBackUpDb.put('pwaData', pwaData);
    }
  }

  async deleteOldPWABackUpData() {
    const tx = this.pwaBackUpDb.transaction('pwaData', 'readwrite');
    const pwaDataStore = tx.objectStore('pwaData');
    const index = pwaDataStore.index('createdDate');

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    thirtyDaysAgo.setHours(0, 0, 0, 0);

    let cursor = await index.openCursor(IDBKeyRange.upperBound(thirtyDaysAgo));

    while (cursor) {
      await pwaDataStore.delete(cursor.primaryKey);
      cursor = await cursor.continue();
    }

    await tx.done;
  }

  // General
  async initSyncStatus() {
    const syncStatus = await this.getSyncStatus();
    if (!syncStatus) {
      await this.db.add('syncStatus', {
        syncId: 1,
        syncStatus: 'Not Synced',
        lastSynced: new Date(),
      });
    }
  }

  async updateSyncStatus(syncStatus: SyncStatus) {
    await this.db.put('syncStatus', syncStatus);
  }

  async getSyncStatus() {
    return await this.db.getFromIndex('syncStatus', 'syncId', 1);
  }

  // Users

  async getUser(userId: string): Promise<User | undefined> {
    return await this.db.get('users', userId);
  }

  async getCurrentUser(): Promise<User[]> {
    return await this.db.getAll('users');
  }

  async getUserRole(userId: string): Promise<string | undefined> {
    const user = await this.db.get('users', userId);
    return user?.userRoleName;
  }

  async addUser(user: User): Promise<string> {
    return await this.db.add('users', user);
  }

  async deleteUser(userId: string) {
    await this.db.delete('users', userId);
  }

  async updateUser(user: User) {
    await this.db.put('users', user);
  }

  // Sales Calendar

  async addSalesCalendarEvent(
    salesCalendarEvent: SalesCalendarEvent,
    skipModified?: boolean
  ): Promise<number> {
    if (!skipModified && salesCalendarEvent.jobaddressid) {
      await this.setJobAddressAsModified(
        Number(salesCalendarEvent.jobaddressid),
        'addSalesCalendarEvent'
      );
    }
    return await this.db.add('salesCalendar', salesCalendarEvent);
  }

  async getAllSalesCalendarEvents(
    userId: string
  ): Promise<SalesCalendarEvent[]> {
    const allSalesCalendarEvents = await this.db.getAllFromIndex(
      'salesCalendar',
      'assignedTo',
      userId
    );

    allSalesCalendarEvents.sort((a, b) => {
      if (a.startTimeUTC < b.startTimeUTC) {
        return -1;
      }
      if (a.startTimeUTC > b.startTimeUTC) {
        return 1;
      }
      return 0;
    });

    return Promise.resolve(allSalesCalendarEvents);
  }

  async getSalesCalendarEventsByJobAddressId(
    jobAddressId: number
  ): Promise<SalesCalendarEvent[]> {
    return await this.db.getAllFromIndex(
      'salesCalendar',
      'jobaddressid',
      jobAddressId
    );
  }

  // Customers

  async addCustomer(
    customer: Customer,
    skipModified?: boolean,
    jobAddressId?: number
  ): Promise<number> {
    if (!skipModified && jobAddressId) {
      await this.setJobAddressAsModified(jobAddressId, 'addCustomer');
    }
    return await this.db.add('customers', customer);
  }

  async getCustomerById(customerId: number): Promise<Customer | undefined> {
    return await this.db.getFromIndex('customers', 'customerid', customerId);
  }

  async getAllCustomers(): Promise<Customer[]> {
    return await this.db.getAll('customers');
  }

  async updateCustomer(
    customer: Customer,
    skipModified?: boolean,
    jobAddressId?: number
  ) {
    if (!skipModified && jobAddressId) {
      await this.setJobAddressAsModified(jobAddressId, 'updateCustomer');
    }
    await this.db.put('customers', customer);
  }

  // Job Addressess

  async addJobAddress(
    jobAddress: JobAddress,
    skipModified?: boolean
  ): Promise<number> {
    if (!skipModified && jobAddress.jobAddressId) {
      await this.setJobAddressAsModified(
        jobAddress.jobAddressId,
        'addJobAddress'
      );
    }
    return await this.db.add('jobAddresses', jobAddress);
  }

  async getJobAddressById(jobAddressId: number) {
    return await this.db.getFromIndex(
      'jobAddresses',
      'jobAddressId',
      jobAddressId
    );
  }

  async getJobAddressesByCustomerId(customerId: number) {
    return await this.db.getAllFromIndex(
      'jobAddresses',
      'customerId',
      customerId
    );
  }

  async getAllJobAddresses(): Promise<JobAddress[]> {
    return await this.db.getAll('jobAddresses');
  }

  async getAllModifiedJobAddresses(): Promise<JobAddress[]> {
    return await this.db.getAllFromIndex('jobAddresses', 'isModified', 1);
  }

  async getAllJobAddressIds(): Promise<number[]> {
    return await this.db.getAllKeys('jobAddresses');
  }

  async setJobAddressAsModified(jobAddressId: number, source: string) {
    // console.log('setJobAddressAsModified', jobAddressId, source);
    const jobAddress = await this.getJobAddressById(jobAddressId);
    if (jobAddress) {
      jobAddress.isModified = 1;
      await this.db.put('jobAddresses', jobAddress);
    }
  }

  async updateJobAddress(jobAddress: JobAddress) {
    if (jobAddress.jobAddressId) {
      await this.setJobAddressAsModified(
        jobAddress.jobAddressId,
        'updateJobAddress'
      );
    }
    await this.db.put('jobAddresses', jobAddress);
  }

  // Appointments

  async addAppointment(
    appointment: Appointment,
    skipModified?: boolean
  ): Promise<number> {
    if (!skipModified && appointment.jobAddressId) {
      await this.setJobAddressAsModified(
        appointment.jobAddressId,
        'addAppointment'
      );
    }
    return await this.db.add('appointments', appointment);
  }

  async getAppointmentById(
    appointmentId: number
  ): Promise<Appointment | undefined> {
    return await this.db.getFromIndex(
      'appointments',
      'appointmentId',
      appointmentId
    );
  }

  async getAppointmentsByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'appointments',
      'jobAddressId',
      jobAddressId
    );
  }

  async getAppointmentsNeedingDisposition() {
    const appointmentsNeedingDisposition: Appointment[] = [];

    const lockoutTimerDate = new Date();
    const noDispoLockoutTimer = Number(
      localStorage.getItem('no_Bid_Lockout_Timer')
    );

    lockoutTimerDate.setDate(lockoutTimerDate.getDate() - noDispoLockoutTimer);

    // effectiveScheduledDate is stored as 'YYYY-MM-DD'
    const formattedLockoutTimerDate = lockoutTimerDate
      .toISOString()
      .split('T')[0];

    const tx = this.db.transaction('appointments', 'readonly');
    const store = tx.objectStore('appointments');

    // Use new index
    const index = store.index('effectiveScheduledDate');

    // Keep the same lower bound you had, but now it must be 'YYYY-MM-DD'
    const allAppointmentsGreaterThanThreeDays = await index.getAll(
      IDBKeyRange.bound('2023-09-11', formattedLockoutTimerDate)
    );

    for (const appointment of allAppointmentsGreaterThanThreeDays) {
      const tx2 = this.db.transaction('customerVisits', 'readonly');
      const store2 = tx2.objectStore('customerVisits');
      const index2 = store2.index('appointmentId');

      const customerVisit = await index2.get(appointment.appointmentId!);

      if (!customerVisit) {
        appointmentsNeedingDisposition.push(appointment);
      } else if (
        customerVisit.dispositionId == null &&
        customerVisit.status !== 2
      ) {
        appointmentsNeedingDisposition.push(appointment);
      }
    }

    return appointmentsNeedingDisposition;
  }

  // async getAppointmentsNeedingDisposition() {
  //   const appointmentsNeedingDisposition: Appointment[] = [];

  //   const lockoutTimerDate = new Date();
  //   let noDispoLockoutTimer = Number(
  //     localStorage.getItem('no_Bid_Lockout_Timer')
  //   );
  //   lockoutTimerDate.setDate(lockoutTimerDate.getDate() - noDispoLockoutTimer);
  //   const formattedLockoutTimerDate = lockoutTimerDate.toISOString();

  //   const tx = this.db.transaction('appointments', 'readonly');
  //   const store = tx.objectStore('appointments');
  //   const index = store.index('date');

  //   // const allAppointmentsGreaterThanThreeDays = await index.getAll(
  //   //   IDBKeyRange.upperBound(formattedLockoutTimerDate)
  //   // );

  //   const allAppointmentsGreaterThanThreeDays = await index.getAll(
  //     IDBKeyRange.bound('2023-09-11', formattedLockoutTimerDate)
  //   );

  //   for (const appointment of allAppointmentsGreaterThanThreeDays) {
  //     const tx2 = this.db.transaction('customerVisits', 'readonly');
  //     const store2 = tx2.objectStore('customerVisits');
  //     const index2 = store2.index('appointmentId');
  //     const customerVisit = await index2.get(appointment.appointmentId!);
  //     tx2.done;

  //     if (!customerVisit) {
  //       appointmentsNeedingDisposition.push(appointment);
  //     } else if (
  //       (customerVisit.dispositionId === undefined &&
  //         customerVisit.status !== 2) ||
  //       (customerVisit.dispositionId === null && customerVisit.status !== 2)
  //     ) {
  //       appointmentsNeedingDisposition.push(appointment);
  //     }
  //     tx2.done;
  //   }
  //   tx.done;

  //   return appointmentsNeedingDisposition;
  // }

  // Leads

  async addLead(lead: Lead, skipModified?: boolean): Promise<number> {
    if (!skipModified && lead.jobAddressId) {
      await this.setJobAddressAsModified(lead.jobAddressId, 'addLead');
    }
    return await this.db.put('leads', lead);
  }

  async getLeadById(leadId: number): Promise<Lead | undefined> {
    return await this.db.getFromIndex('leads', 'leadId', leadId);
  }

  async getLeadsByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex('leads', 'jobAddressId', jobAddressId);
  }

  // Customer Visits
  async addCustomerVisit(
    customerVisit: CustomerVisit,
    skipModified?: boolean
  ): Promise<string> {
    if (!skipModified && customerVisit.jobAddressId) {
      await this.setJobAddressAsModified(
        customerVisit.jobAddressId,
        'addCustomerVisit'
      );
    }
    return await this.db.add('customerVisits', customerVisit);
  }

  async getCustomerVisitById(
    customerVisitId: string
  ): Promise<CustomerVisit | undefined> {
    return await this.db.getFromIndex(
      'customerVisits',
      'customerVisitId',
      customerVisitId
    );
  }

  async getCustomerVisitByAppointmentId(
    appointmentId: number
  ): Promise<CustomerVisit | undefined> {
    return await this.db.getFromIndex(
      'customerVisits',
      'appointmentId',
      appointmentId
    );
  }

  async getCustomerVisitsByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'customerVisits',
      'jobAddressId',
      jobAddressId
    );
  }

  async updateCustomerVisit(
    customerVisit: CustomerVisit,
    skipModified?: boolean
  ) {
    if (!skipModified && customerVisit.jobAddressId) {
      await this.setJobAddressAsModified(
        customerVisit.jobAddressId,
        'updateCustomerVisit'
      );
    }
    await this.db.put('customerVisits', customerVisit);
  }

  async getAllCustomerVisits() {
    return await this.db.getAll('customerVisits');
  }

  async getCustomerVisitsByStatus(status: number) {
    const tx = this.db.transaction('customerVisits', 'readonly');
    const store = tx.objectStore('customerVisits');
    const index = store.index('status');

    const allCustomerVisitsByStatus = await index.getAll(status);

    allCustomerVisitsByStatus.forEach(async (customerVisit) => {
      const tx2 = this.db.transaction('salesCalendar', 'readonly');
      const store2 = tx2.objectStore('salesCalendar');
      const index2 = store2.index('appointmentId');
      const salesCalendarEvent = await index2.get(customerVisit.appointmentId!);

      if (salesCalendarEvent) {
        customerVisit.customerName = salesCalendarEvent.customerName;
        customerVisit.jobAddress = salesCalendarEvent.jobAddress;
      }
    });

    return allCustomerVisitsByStatus;
  }

  async checkIfOverHotQuoteLimit() {
    let hotQuoteLimit = Number(localStorage.getItem('hot_Quote_Limit'));
    let hotQuoteStatus = 8;

    const tx = this.db.transaction('customerVisits', 'readonly');
    const store = tx.objectStore('customerVisits');
    const index = store.index('status');

    const allHotQuotes = await index.getAll(hotQuoteStatus);
    tx.done;

    if (allHotQuotes.length > hotQuoteLimit) {
      return true;
    } else {
      return false;
    }
  }

  async checkForSoldJobsNeedingSoldSheet() {
    const soldStatus = 2;
    const tx = this.db.transaction('customerVisits', 'readonly');
    const store = tx.objectStore('customerVisits');
    const index = store.index('status');

    const startDate = new Date('2023-11-01');

    const allSoldJobs = await index.getAll(soldStatus);
    tx.done;

    const jobsNeedingSoldSheet: CustomerVisit[] = [];

    for (const job of allSoldJobs) {
      const tx2 = this.db.transaction('soldSheets', 'readonly');
      const store2 = tx2.objectStore('soldSheets');
      const index2 = store2.index('jobAddressId');
      const soldSheet = await index2.get(job.jobAddressId);
      const createdDate = new Date(job.createdDate);

      if (!soldSheet && createdDate > startDate) {
        jobsNeedingSoldSheet.push(job);
      }
      tx2.done;
    }

    return jobsNeedingSoldSheet;
  }

  async checkForSoldServiceProposalsNeedingSoldSheet() {
    const soldStatus = 2;
    const tx = this.db.transaction('proposals', 'readonly');
    const store = tx.objectStore('proposals');
    const index = store.index('status');

    const startDate = new Date('2023-11-01');

    const allSoldProposals = await index.getAll(soldStatus);
    tx.done;

    const serviceProposalsNeedingSoldSheet: ServiceVisit[] = [];

    for (const proposal of allSoldProposals) {
      if (proposal.functionName === 'Service Visit') {
        const tx2 = this.db.transaction('soldSheets', 'readonly');
        const store2 = tx2.objectStore('soldSheets');
        const index2 = store2.index('jobAddressId');
        const soldSheet = await index2.get(proposal.jobAddressId);
        const createdDate = new Date(proposal.createdDate);

        if (!soldSheet && createdDate > startDate) {
          const tx3 = this.db.transaction('serviceVisits', 'readonly');
          const store3 = tx3.objectStore('serviceVisits');
          const index3 = store3.index('serviceVisitId');
          const serviceVisit = await index3.get(proposal.functionId!);
          if (serviceVisit) {
            serviceProposalsNeedingSoldSheet.push(serviceVisit);
          }
          tx3.done;
        }
        tx2.done;
      }
    }

    return serviceProposalsNeedingSoldSheet;
  }

  // Proposals
  async addProposal(
    proposal: Proposal,
    skipModified?: boolean
  ): Promise<string> {
    if (!skipModified && proposal.jobAddressId) {
      await this.setJobAddressAsModified(proposal.jobAddressId, 'addProposal');
    }
    return await this.db.put('proposals', proposal);
  }

  async deleteProposal(proposalId: string, skipModified?: boolean) {
    if (!skipModified) {
      const proposal = await this.getProposalById(proposalId);
      if (proposal && proposal.jobAddressId) {
        await this.setJobAddressAsModified(
          proposal.jobAddressId,
          'deleteProposal'
        );
      }
    }
    await this.db.delete('proposals', proposalId);
  }

  async getProposalById(proposalId: string): Promise<Proposal | undefined> {
    return await this.db.getFromIndex('proposals', 'proposalId', proposalId);
  }

  async getProposalsByCustomerVisitId(
    customerVisitId: string
  ): Promise<Proposal[]> {
    return this.db.getAllFromIndex(
      'proposals',
      'customerVisitId',
      customerVisitId
    );
  }

  // async updateUnsoldProposals(customerVisitId: string, proposalId: string) {
  //   const proposals = await this.db.getAllFromIndex(
  //     'proposals',
  //     'customerVisitId',
  //     customerVisitId
  //   );
  //   proposals.forEach(async (proposal) => {
  //     if (proposal.proposalId !== proposalId) {
  //       proposal.status = 6;
  //       proposal.proposalStatusName = 'Not Used';
  //       await this.db.put('proposals', proposal);
  //     }
  //   });
  // }

  getProposalsByJobAddressId(jobAddressId: number): Promise<Proposal[]> {
    return this.db.getAllFromIndex('proposals', 'jobAddressId', jobAddressId);
  }

  async updateProposal(proposal: Proposal, skipModified?: boolean) {
    if (!skipModified && proposal.jobAddressId) {
      await this.setJobAddressAsModified(
        proposal.jobAddressId,
        'updateProposal'
      );
    }
    return this.db.put('proposals', proposal);
  }

  getAllProposals(): Promise<Proposal[]> {
    return this.db.getAll('proposals');
  }

  getProposalsByStatus(status: number): Promise<Proposal[]> {
    return this.db.getAllFromIndex('proposals', 'status', status);
  }

  getProposalsByAppointmentId(appointmentId: number): Promise<Proposal[]> {
    return this.db.getAllFromIndex('proposals', 'appointmentId', appointmentId);
  }

  // Service
  async checkForServiceProposalsToSendToInsideSales() {
    const proposalsToBeSentToInsideSales: Proposal[] = [];
    // reference checkForProdAppointmentsNeedingDailies if this ever needs to be changed
    // I think the date here may be off slightly due to the time.
    const appointmentCutOffDate = new Date();
    appointmentCutOffDate.setDate(appointmentCutOffDate.getDate() - 7);
    const formattedAppointmentCutOffDate = appointmentCutOffDate.toISOString();

    const userName = localStorage.getItem('name');

    const tx = this.db.transaction('serviceAppointments', 'readonly');
    const store = tx.objectStore('serviceAppointments');
    const index = store.index('scheduledDate');

    const allServiceAppointmentsOlderThanSevenDays = await index.getAll(
      IDBKeyRange.upperBound(formattedAppointmentCutOffDate)
    );

    for (const appointment of allServiceAppointmentsOlderThanSevenDays) {
      const tx2 = this.db.transaction('proposals', 'readonly');
      const store2 = tx2.objectStore('proposals');
      const index2 = store2.index('jobAddressId');

      const proposal = await index2.get(appointment.jobAddressId);
      if (proposal && proposal.status !== 2 && proposal.createdBy == userName) {
        proposalsToBeSentToInsideSales.push(proposal);
      }
      tx2.done;
    }
    tx.done;

    return proposalsToBeSentToInsideSales;
  }

  // Products
  async addProductSubCategory(
    productSubCategory: ProductSubCategory
  ): Promise<number> {
    return await this.db.add('products', productSubCategory);
  }

  async getAllProducts(): Promise<ProductSubCategory[]> {
    return await this.db.getAll('products');
  }

  // Statements
  async addStatement(statement: Statements): Promise<number> {
    return await this.db.add('statements', statement);
  }

  async getAllStatements(): Promise<Statements[]> {
    return await this.db.getAll('statements');
  }

  // Get statements by proposal

  // Contracts
  async addContract(
    contract: Contract,
    skipModified?: boolean
  ): Promise<number> {
    if (!skipModified && contract.jobAddressId) {
      await this.setJobAddressAsModified(contract.jobAddressId, 'addContract');
    }
    return await this.db.put('contracts', contract);
  }

  async getContractById(contractId: number): Promise<Contract | undefined> {
    return await this.db.getFromIndex('contracts', 'contractId', contractId);
  }

  async getContractsByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'contracts',
      'jobAddressId',
      jobAddressId
    );
  }

  getContractByProposalId(proposalId: string): Promise<Contract | undefined> {
    return this.db.getFromIndex('contracts', 'proposalId', proposalId);
  }

  async getNewContractsByJobAddressId(jobAddressId: number) {
    const newContracts: Contract[] = [];

    const idRange = IDBKeyRange.upperBound(0, true);
    const tx = this.db.transaction(['contracts'], 'readonly');
    const store = tx.objectStore('contracts');
    const index = store.index('contractId');
    let cursor = await index.openCursor(idRange);
    while (cursor) {
      if (cursor.value.jobAddressId === jobAddressId) {
        newContracts.push(cursor.value);
      }
      cursor = await cursor.continue();
    }
    return await newContracts;
  }

  async getContractsByCustomerVisitId(
    customerVisitId: string
  ): Promise<Contract[]> {
    return await this.db.getAllFromIndex(
      'contracts',
      'customerVisitId',
      customerVisitId
    );
  }

  async deleteContract(contractId: number) {
    await this.db.delete('contracts', contractId);
  }

  // Notes
  async addNote(note: Note, skipModified?: boolean): Promise<number> {
    if (!skipModified && note.jobAddressId) {
      await this.setJobAddressAsModified(note.jobAddressId, 'addNote');
    }
    return await this.db.add('notes', note);
  }

  async getNotesByFunctionId(
    functionId: number | string,
    functionName: string
  ): Promise<Note[]> {
    const notes = await this.db.getAllFromIndex(
      'notes',
      'functionId',
      functionId
    );
    return notes.filter((note) => note.functionName === functionName);
  }

  async getNotesByFunctionName(
    jobAddressId: number,
    functionName: string
  ): Promise<Note[]> {
    const notes = await this.db.getAllFromIndex(
      'notes',
      'functionName',
      functionName
    );
    return notes.filter((note) => note.jobAddressId === jobAddressId);
  }

  async getNotesByJobAddressId(jobAddressId: number): Promise<Note[]> {
    return await this.db.getAllFromIndex('notes', 'jobAddressId', jobAddressId);
  }

  async getNotesByCustomerId(customerId: number): Promise<Note[]> {
    return await this.db.getAllFromIndex('notes', 'customerId', customerId);
  }

  async getNewNotesByJobAddressId(jobAddressId: number): Promise<Note[]> {
    const notes: Note[] = [];

    const idbRange = IDBKeyRange.upperBound(0, true);
    const tx = this.db.transaction('notes', 'readonly');
    const store = tx.objectStore('notes');
    const index = store.index('noteId');
    let cursor = await index.openCursor(idbRange);
    while (cursor) {
      const note = cursor.value;
      if (note.jobAddressId === jobAddressId) {
        notes.push(note);
      }
      cursor = await cursor.continue();
    }
    return await notes;
  }

  // Attachments
  async addAttachment(
    attachment: Attachment,
    skipModified?: boolean
  ): Promise<number> {
    if (!skipModified && attachment.jobAddressId) {
      await this.setJobAddressAsModified(
        attachment.jobAddressId,
        'addAttachment'
      );
    }
    return await this.db.add('attachments', attachment);
  }

  async getAttachmentsByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'attachments',
      'jobAddressId',
      jobAddressId
    );
  }

  async getAttachmentByAttachmentId(attachmentId: number) {
    return await this.db.getFromIndex(
      'attachments',
      'attachmentId',
      attachmentId
    );
  }

  async getAttachmentsByFunctionId(functionId: string) {
    return await this.db.getAllFromIndex(
      'attachments',
      'functionId',
      functionId
    );
  }

  async getNewAttachmentsByJobAddressId(jobAddressId: number) {
    const attachments: Attachment[] = [];

    const idRange = IDBKeyRange.upperBound(0, true);
    const tx = this.db.transaction('attachments', 'readonly');
    const store = tx.objectStore('attachments');
    const index = store.index('attachmentId');
    let cursor = await index.openCursor(idRange);
    while (cursor) {
      const attachment = cursor.value;
      if (attachment.jobAddressId === jobAddressId) {
        attachments.push(attachment);
      }
      cursor = await cursor.continue();
    }
    return await attachments;
  }

  async getAllNewAttachments() {
    const attachments: Attachment[] = [];

    const idRange = IDBKeyRange.upperBound(0, true);
    const tx = this.db.transaction(['attachments'], 'readonly');
    const store = tx.objectStore('attachments');
    const index = store.index('attachmentId');
    let cursor = await index.openCursor(idRange);
    while (cursor) {
      attachments.push(cursor.value);
      cursor = await cursor.continue();
    }
    return await attachments;
  }

  async updateAttachmentLabel(attachment: Attachment) {
    await this.db.put('attachments', attachment);
  }

  async getContractAttachmentsByContractId(contractId: number) {
    return await this.db.getFromIndex('attachments', 'functionId', contractId.toString());
  }

  async getDrawingAttachmentsByJobAddressId(jobAddressId: number) {
    const attachments: Attachment[] = await this.db.getAllFromIndex(
      'attachments',
      'jobAddressId',
      jobAddressId
    );
    return attachments.filter((attachment) => attachment.isDrawing === true);
  }

  // Generated Contracts
  async addGeneratedContract(
    generatedContract: GeneratedContract,
    skipModified?: boolean
  ) {
    if (!skipModified && generatedContract.jobAddressId) {
      await this.setJobAddressAsModified(
        generatedContract.jobAddressId,
        'addGeneratedContract'
      );
    }
    return await this.db.put('generatedContracts', generatedContract);
  }

  async getNewGeneratedContractsByJobAddressId(jobAddressId: number) {
    const generatedContracts: GeneratedContract[] = [];

    const idRange = IDBKeyRange.upperBound(0, true);
    const tx = this.db.transaction(['generatedContracts'], 'readonly');
    const store = tx.objectStore('generatedContracts');
    const index = store.index('id');
    let cursor = await index.openCursor(idRange);
    while (cursor) {
      if (cursor.value.jobAddressId === jobAddressId) {
        generatedContracts.push(cursor.value);
      }
      cursor = await cursor.continue();
    }
    return await generatedContracts;
  }

  async getGeneratedContractsByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'generatedContracts',
      'jobAddressId',
      jobAddressId
    );
  }

  async getGeneratedContractByContractId(contractId: number) {
    return this.db.getFromIndex('generatedContracts', 'contractId', contractId);
  }

  async deleteGeneratedContract(generatedContractId: number) {
    await this.db.delete('generatedContracts', generatedContractId);
  }

  // Sold Sheets
  async addSoldSheet(soldSheet: SoldSheet, skipModified?: boolean) {
    if (!skipModified && soldSheet.jobAddressId) {
      await this.setJobAddressAsModified(
        soldSheet.jobAddressId,
        'addSoldSheet'
      );
    }
    return await this.db.put('soldSheets', soldSheet);
  }

  async getSoldSheetsByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'soldSheets',
      'jobAddressId',
      jobAddressId
    );
  }

  async getSoldSheetByContractId(contractId: number) {
    return await this.db.getFromIndex('soldSheets', 'contractId', contractId);
  }

  async getSoldSheetByProposalId(proposalId: string) {
    return await this.db.getFromIndex('soldSheets', 'proposalId', proposalId);
  }

  // Prod Availability
  async addProdAvailability(
    prodAvailability: ProdAvailability,
    skipModified?: boolean
  ) {
    if (!skipModified && prodAvailability.jobAddressId) {
      await this.setJobAddressAsModified(
        prodAvailability.jobAddressId,
        'addProdAvailability'
      );
    }
    return await this.db.add('prodAvailability', prodAvailability);
  }

  async getAllProdAvailability(userId: string): Promise<ProdAvailability[]> {
    const allProdAvailability = await this.db.getAllFromIndex(
      'prodAvailability',
      'assignedToId',
      userId
    );

    allProdAvailability.sort((a, b) => {
      if (a.startTimeUTC < b.startTimeUTC) {
        return -1;
      }
      if (a.startTimeUTC > b.startTimeUTC) {
        return 1;
      }
      return 0;
    });

    return Promise.resolve(allProdAvailability);
  }

  async getProdAvailabilityByJobAddressId(
    jobAddressId: number
  ): Promise<ProdAvailability[]> {
    return await this.db.getAllFromIndex(
      'prodAvailability',
      'jobAddressId',
      jobAddressId
    );
  }

  // Prod Appointments
  async addProdAppointment(
    prodAppointment: ProdAppointment,
    skipModified?: boolean
  ) {
    if (!skipModified && prodAppointment.jobAddressId) {
      await this.setJobAddressAsModified(
        prodAppointment.jobAddressId,
        'addProdAppointment'
      );
    }
    return await this.db.add('prodAppointments', prodAppointment);
  }

  async getProdAppointmentById(
    prodSchedId: number
  ): Promise<ProdAppointment | undefined> {
    return await this.db.getFromIndex(
      'prodAppointments',
      'prodSchedId',
      prodSchedId
    );
  }

  async getProdAppointmentsByJobAddressId(
    jobAddressId: number
  ): Promise<ProdAppointment[]> {
    return await this.db.getAllFromIndex(
      'prodAppointments',
      'jobAddressId',
      jobAddressId
    );
  }

  async getProdAppointmentsByContractId(
    contractId: number
  ): Promise<ProdAppointment[]> {
    return await this.db.getAllFromIndex(
      'prodAppointments',
      'contractId',
      contractId
    );
  }

  // Projects
  async addProject(project: Project, skipModified?: boolean) {
    if (!skipModified && project.jobAddressId) {
      await this.setJobAddressAsModified(project.jobAddressId, 'addProject');
    }
    return await this.db.add('projects', project);
  }

  async getProjectById(projectId: number): Promise<Project | undefined> {
    return await this.db.getFromIndex('projects', 'projectId', projectId);
  }

  async getProjectsByJobAddressId(jobAddressId: number): Promise<Project[]> {
    return await this.db.getAllFromIndex(
      'projects',
      'jobAddressId',
      jobAddressId
    );
  }

  async getProjectsByContractId(contractId: number): Promise<Project[]> {
    return await this.db.getAllFromIndex('projects', 'contractId', contractId);
  }

  async getProjectsByStatus(status: number): Promise<Project[]> {
    return await this.db.getAllFromIndex('projects', 'status', status);
  }

  async updateProject(project: Project, skipModified?: boolean) {
    if (!skipModified && project.jobAddressId) {
      await this.setJobAddressAsModified(project.jobAddressId, 'updateProject');
    }
    await this.db.put('projects', project);
  }

  // Production Visits/FullProduction
  async addProductionVisit(
    productionVisit: Production,
    skipModified?: boolean
  ) {
    if (!skipModified && productionVisit.jobAddressId) {
      await this.setJobAddressAsModified(
        productionVisit.jobAddressId,
        'addProductionVisit'
      );
    }
    return await this.db.put('productionVisits', productionVisit);
  }

  async getProductionById(productionId: string) {
    return await this.db.getFromIndex(
      'productionVisits',
      'productionId',
      productionId
    );
  }

  async getProductionByProdSchedId(prodSchedId: number) {
    return await this.db.getFromIndex(
      'productionVisits',
      'prodSchedId',
      prodSchedId
    );
  }

  async getProductionByProjectId(projectId: number) {
    return await this.db.getAllFromIndex(
      'productionVisits',
      'projectId',
      projectId
    );
  }

  async getProductionByContractId(contractId: number) {
    return await this.db.getAllFromIndex(
      'productionVisits',
      'contractId',
      contractId
    );
  }

  async getProductionByStatus(status: number) {
    return await this.db.getAllFromIndex('productionVisits', 'status', status);
  }

  async getProductionByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'productionVisits',
      'jobAddressId',
      jobAddressId
    );
  }

  async updateProductionVisit(
    productionVisit: Production,
    skipModified?: boolean
  ) {
    if (!skipModified && productionVisit.jobAddressId) {
      await this.setJobAddressAsModified(
        productionVisit.jobAddressId,
        'updateProductionVisit'
      );
    }
    return await this.db.put('productionVisits', productionVisit);
  }

  // Change Orders

  async addChangeOrder(changeOrder: ChangeOrder, skipModified?: boolean) {
    if (!skipModified && changeOrder.jobAddressId) {
      await this.setJobAddressAsModified(
        changeOrder.jobAddressId,
        'addChangeOrder'
      );
    }
    return await this.db.put('changeOrders', changeOrder);
  }

  async getChangeOrderById(changeOrderId: string) {
    return await this.db.getFromIndex(
      'changeOrders',
      'changeOrderId',
      changeOrderId
    );
  }

  async getChangeOrdersByFunctionId(functionId: string) {
    return await this.db.getAllFromIndex(
      'changeOrders',
      'functionId',
      functionId
    );
  }

  async getChangeOrdersByJobAddressId(jobAddressId: number) {
    const tx = this.db.transaction('changeOrders', 'readonly');
    const store = tx.objectStore('changeOrders');
    const index = store.index('jobAddressId');

    const changeOrders = await index.getAll(jobAddressId);
    const approvedChangeOrders: ChangeOrder[] = [];

    changeOrders.forEach(async (changeOrder) => {
      if (changeOrder.status === 1) {
        approvedChangeOrders.push(changeOrder);
      }
    });

    return approvedChangeOrders;
  }

  async getChangeOrdersByProjectId(projectId: number) {
    return await this.db.getAllFromIndex(
      'changeOrders',
      'projectid',
      projectId
    );
  }

  async deleteChangeOrder(changeOrderId: string) {
    return await this.db.delete('changeOrders', changeOrderId);
  }

  // Upsells

  async addUpsell(upsell: Upsell, skipModified?: boolean) {
    if (!skipModified && upsell.jobAddressId) {
      await this.setJobAddressAsModified(upsell.jobAddressId, 'addUpsell');
    }
    return await this.db.put('upsells', upsell);
  }

  async getUpsellById(upsellId: string) {
    return await this.db.getFromIndex('upsells', 'upsellId', upsellId);
  }

  async getUpsellsByFunctionId(functionId: string) {
    return await this.db.getAllFromIndex('upsells', 'functionId', functionId);
  }

  async getUpsellsByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'upsells',
      'jobAddressId',
      jobAddressId
    );
  }

  async getUpsellByProjectId(projectId: number) {
    return await this.db.getFromIndex('upsells', 'projectId', projectId);
  }

  async updateUpsell(upsell: Upsell, skipModified?: boolean) {
    if (!skipModified && upsell.jobAddressId) {
      await this.setJobAddressAsModified(upsell.jobAddressId, 'updateUpsell');
    }
    return await this.db.put('upsells', upsell);
  }

  async deleteUpsell(upsellId: string, skipModified?: boolean) {
    if (!skipModified) {
      const upsell = await this.getUpsellById(upsellId);
      if (upsell && upsell.jobAddressId) {
        await this.setJobAddressAsModified(upsell.jobAddressId, 'deleteUpsell');
      }
    }
    return await this.db.delete('upsells', upsellId);
  }

  // Wrap Ups
  async addWrapUp(wrapUp: WrapUp, skipModified?: boolean) {
    if (!skipModified && wrapUp.jobAddressId) {
      await this.setJobAddressAsModified(wrapUp.jobAddressId, 'addWrapUp');
    }
    return await this.db.add('wrapUps', wrapUp);
  }

  async getWrapUpsByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'wrapUps',
      'jobAddressId',
      jobAddressId
    );
  }

  async getWrapUpByFunctionId(functionId: string) {
    return await this.db.getFromIndex('wrapUps', 'functionId', functionId);
  }

  async updateWrapUp(wrapUp: WrapUp, skipModified?: boolean) {
    if (!skipModified && wrapUp.jobAddressId) {
      await this.setJobAddressAsModified(wrapUp.jobAddressId, 'updateWrapUp');
    }
    return await this.db.put('wrapUps', wrapUp);
  }

  // Service Availability
  async addServiceAvailability(
    serviceAvailability: ServiceAvailability,
    skipModified?: boolean
  ) {
    if (!skipModified && serviceAvailability.jobAddressId) {
      await this.setJobAddressAsModified(
        serviceAvailability.jobAddressId,
        'addServiceAvailability'
      );
    }
    return await this.db.add('serviceAvailability', serviceAvailability);
  }

  async getAllServiceAvailability(userId: string) {
    const allServiceAvailability = await this.db.getAllFromIndex(
      'serviceAvailability',
      'assignedToId',
      userId
    );

    allServiceAvailability.sort((a, b) => {
      if (a.startTimeUTC < b.startTimeUTC) {
        return -1;
      }
      if (a.startTimeUTC > b.startTimeUTC) {
        return 1;
      }
      return 0;
    });

    return Promise.resolve(allServiceAvailability);
  }

  async getServiceAvailabilityByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'serviceAvailability',
      'jobAddressId',
      jobAddressId
    );
  }

  // Service Appointments
  async addServiceAppointment(
    serviceAppointment: ServiceAppointment,
    skipModified?: boolean
  ) {
    if (!skipModified && serviceAppointment.jobAddressId) {
      await this.setJobAddressAsModified(
        serviceAppointment.jobAddressId,
        'addServiceAppointment'
      );
    }
    return await this.db.add('serviceAppointments', serviceAppointment);
  }

  async getServiceAppointmentById(serviceSchedId: number) {
    return await this.db.getFromIndex(
      'serviceAppointments',
      'servAppointmentId',
      serviceSchedId
    );
  }

  async getServiceAppointmentsByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'serviceAppointments',
      'jobAddressId',
      jobAddressId
    );
  }

  // Service Projects
  async addServiceProject(
    serviceProject: ServiceProject,
    skipModified?: boolean
  ) {
    if (!skipModified && serviceProject.jobAddressId) {
      await this.setJobAddressAsModified(
        serviceProject.jobAddressId,
        'addServiceProject'
      );
    }
    return await this.db.add('serviceProjects', serviceProject);
  }

  async getServiceProjectById(serviceProjectId: number) {
    return await this.db.getFromIndex(
      'serviceProjects',
      'serviceProjectId',
      serviceProjectId
    );
  }

  async getServiceProjectsByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'serviceProjects',
      'jobAddressId',
      jobAddressId
    );
  }

  async getServiceProjectsByStatus(status: number) {
    return await this.db.getAllFromIndex('serviceProjects', 'status', status);
  }

  // Service Visits
  async addServiceVisit(serviceVisit: ServiceVisit, skipModified?: boolean) {
    if (!skipModified && serviceVisit.jobaddressId) {
      await this.setJobAddressAsModified(
        serviceVisit.jobaddressId,
        'addServiceVisit'
      );
    }
    return await this.db.add('serviceVisits', serviceVisit);
  }

  async getServiceVisitById(serviceVisitId: string) {
    return await this.db.getFromIndex(
      'serviceVisits',
      'serviceVisitId',
      serviceVisitId
    );
  }

  async getServiceVisitByServiceAppointmentId(servAppointmentId: number) {
    return await this.db.getFromIndex(
      'serviceVisits',
      'serviceAppointmentId',
      servAppointmentId
    );
  }

  async getServiceVisitsByProjectId(serviceProjectId: number) {
    return await this.db.getAllFromIndex(
      'serviceVisits',
      'serviceProjectId',
      serviceProjectId
    );
  }

  async getServiceVisitsByStatus(status: number) {
    return await this.db.getAllFromIndex('serviceVisits', 'status', status);
  }

  async getServiceVisitsByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'serviceVisits',
      'jobaddressId',
      jobAddressId
    );
  }

  async updateServiceVisit(serviceVisit: ServiceVisit, skipModified?: boolean) {
    if (!skipModified && serviceVisit.jobaddressId) {
      await this.setJobAddressAsModified(
        serviceVisit.jobaddressId,
        'updateServiceVisit'
      );
    }
    return await this.db.put('serviceVisits', serviceVisit);
  }

  // GeoZonesFull

  async addGeoZoneFull(geoZoneFull: GeoZoneFull) {
    return await this.db.add('geoZonesFull', geoZoneFull);
  }

  async getAllGeoZonesFull() {
    return await this.db.getAll('geoZonesFull');
  }

  // Accounting Info

  async addAccountingInfo(
    accountingInfo: AccountingInfo,
    skipModified?: boolean
  ) {
    // if (!skipModified && accountingInfo.jobAddressId) {
    //   await this.setJobAddressAsModified(
    //     accountingInfo.jobAddressId,
    //     'addAccountingInfo'
    //   );
    // }
    return await this.db.add('accountingInfo', accountingInfo);
  }

  async getAccountingInfoByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'accountingInfo',
      'jobAddressId',
      jobAddressId
    );
  }

  async getAccountingInfoByProjectId(projectId: number) {
    return await this.db.getFromIndex('accountingInfo', 'projectId', projectId);
  }

  // Invoices

  async addInvoice(invoice: Invoice, skipModified?: boolean) {
    if (!skipModified && invoice.jobAddressId) {
      await this.setJobAddressAsModified(invoice.jobAddressId, 'addInvoice');
    }
    return await this.db.put('invoices', invoice);
  }

  async getInvoicesByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'invoices',
      'jobAddressId',
      jobAddressId
    );
  }

  async getInvoiceByProjectId(projectId: number) {
    return await this.db.getFromIndex('invoices', 'projectId', projectId);
  }

  async getInvoicesByFunctionId(functionId: string) {
    return await this.db.getAllFromIndex('invoices', 'functionId', functionId);
  }

  async deleteInvoice(projectId: number) {
    return await this.db.delete('invoices', projectId);
  }

  // Upsell Contracts

  async addUpsellContract(
    upsellContract: UpsellContract,
    skipModified?: boolean
  ) {
    if (!skipModified && upsellContract.jobAddressId) {
      await this.setJobAddressAsModified(
        upsellContract.jobAddressId,
        'addUpsellContract'
      );
    }
    return await this.db.put('upsellContracts', upsellContract);
  }

  async getupsellContractById(upsellContractId: number) {
    return await this.db.getFromIndex(
      'upsellContracts',
      'upsellContractId',
      upsellContractId
    );
  }

  async getUpsellContractByUpsellId(upsellId: string) {
    return await this.db.getFromIndex('upsellContracts', 'upsellId', upsellId);
  }

  async getUpsellContractsByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'upsellContracts',
      'jobAddressId',
      jobAddressId
    );
  }

  async deleteUpsellContract(upsellContractId: number) {
    return await this.db.delete('upsellContracts', upsellContractId);
  }

  // Generated Upsell Contracts

  async addGeneratedUpsellContract(
    generatedUpsellContract: GeneratedUpsellContract,
    skipModified?: boolean
  ) {
    if (!skipModified && generatedUpsellContract.jobAddressId) {
      await this.setJobAddressAsModified(
        generatedUpsellContract.jobAddressId,
        'addGeneratedUpsellContract'
      );
    }
    return await this.db.put(
      'generatedUpsellContracts',
      generatedUpsellContract
    );
  }

  async getGeneratedUpsellContractById(upsellContractId: number) {
    return await this.db.getFromIndex(
      'generatedUpsellContracts',
      'upsellContractId',
      upsellContractId
    );
  }

  async getGeneratedUpsellContractsByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'generatedUpsellContracts',
      'jobAddressId',
      jobAddressId
    );
  }

  async deleteGeneratedUpsellContract(upsellContractId: number) {
    return await this.db.delete('generatedUpsellContracts', upsellContractId);
  }

  // Upsell Appointments

  async addUpsellAppointment(
    upsellAppointment: UpsellAppointment,
    skipModified?: boolean
  ) {
    if (!skipModified && upsellAppointment.jobAddressId) {
      await this.setJobAddressAsModified(
        upsellAppointment.jobAddressId,
        'addUpsellAppointment'
      );
    }
    return await this.db.add('upsellAppointments', upsellAppointment);
  }

  async getAllUpsellAppointments() {
    return await this.db.getAll('upsellAppointments');
  }

  async getUpsellAppointmentById(upsellAppointmentId: number) {
    return await this.db.getFromIndex(
      'upsellAppointments',
      'upsellAppointmentId',
      upsellAppointmentId
    );
  }

  async getUpsellAppointmentsByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'upsellAppointments',
      'jobAddressId',
      jobAddressId
    );
  }

  async deleteUpsellAppointment(upsellAppointmentId: number) {
    return await this.db.delete('upsellAppointments', upsellAppointmentId);
  }

  // Upsell Calendar Events

  async addUpsellCalendarEvent(
    upsellCalendarEvent: UpsellCalendarEvent,
    skipModified?: boolean
  ) {
    if (!skipModified && upsellCalendarEvent.jobAddressId) {
      await this.setJobAddressAsModified(
        upsellCalendarEvent.jobAddressId,
        'addUpsellCalendarEvent'
      );
    }
    return await this.db.add('upsellCalendarEvents', upsellCalendarEvent);
  }

  async getAllUpsellCalendarEvents() {
    return await this.db.getAll('upsellCalendarEvents');
  }

  async getUpsellCalendarEventByUpsellId(upsellId: string) {
    return await this.db.getFromIndex(
      'upsellCalendarEvents',
      'upsellId',
      upsellId
    );
  }

  async updateUpsellCalendarEvent(
    upsellCalendarEvent: UpsellCalendarEvent,
    skipModified?: boolean
  ) {
    if (!skipModified && upsellCalendarEvent.jobAddressId) {
      await this.setJobAddressAsModified(
        upsellCalendarEvent.jobAddressId,
        'updateUpsellCalendarEvent'
      );
    }
    return await this.db.put('upsellCalendarEvents', upsellCalendarEvent);
  }

  async deleteUpsellCalendarEvent(upsellCalendarEventId: number) {
    return await this.db.delete('upsellCalendarEvents', upsellCalendarEventId);
  }

  // Upsell Visits

  async addUpsellVisit(upsellVisit: UpsellVisit, skipModified?: boolean) {
    if (!skipModified && upsellVisit.jobAddressId) {
      await this.setJobAddressAsModified(
        upsellVisit.jobAddressId,
        'addUpsellVisit'
      );
    }
    return await this.db.add('upsellVisits', upsellVisit);
  }

  async getUpsellVisitById(upsellVisitId: string) {
    return await this.db.getFromIndex(
      'upsellVisits',
      'upsellVisitId',
      upsellVisitId
    );
  }

  async getUpsellVisitByUpsellAppointmentId(upsellAppointmentId: number) {
    return await this.db.getFromIndex(
      'upsellVisits',
      'upsellAppointmentId',
      upsellAppointmentId
    );
  }

  async getUpsellVisitByProjectId(projectId: number) {
    return await this.db.getAllFromIndex(
      'upsellVisits',
      'projectId',
      projectId
    );
  }

  async getUpsellVisitsByStatus(status: number) {
    return await this.db.getAllFromIndex('upsellVisits', 'status', status);
  }

  async getUpsellVisitsByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'upsellVisits',
      'jobAddressId',
      jobAddressId
    );
  }

  async updateUpsellVisit(upsellVisit: UpsellVisit, skipModified?: boolean) {
    if (!skipModified && upsellVisit.jobAddressId) {
      await this.setJobAddressAsModified(
        upsellVisit.jobAddressId,
        'updateUpsellVisit'
      );
    }
    return await this.db.put('upsellVisits', upsellVisit);
  }

  async deleteUpsellVisit(upsellVisitId: string) {
    return await this.db.delete('upsellVisits', upsellVisitId);
  }

  // Upsell Products
  async addUpsellProductSubCategory(
    productSubCategory: ProductSubCategory
  ): Promise<number> {
    return await this.db.add('upsellProducts', productSubCategory);
  }

  async getAllUpsellProducts(): Promise<ProductSubCategory[]> {
    return await this.db.getAll('upsellProducts');
  }

  // Change Order Contracts

  async addChangeOrderContract(
    changeOrderContract: ChangeOrderContract,
    skipModified?: boolean
  ) {
    if (!skipModified && changeOrderContract.jobAddressId) {
      await this.setJobAddressAsModified(
        changeOrderContract.jobAddressId,
        'addChangeOrderContract'
      );
    }
    return await this.db.add('changeOrderContracts', changeOrderContract);
  }

  async getChangeOrderContractById(changeOrderContractId: number) {
    return await this.db.getFromIndex(
      'changeOrderContracts',
      'changeOrderContractId',
      changeOrderContractId
    );
  }

  async getChangeOrderContractByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'changeOrderContracts',
      'jobAddressId',
      jobAddressId
    );
  }

  async getChangeOrderContractByChangeOrderId(changeOrderId: string) {
    return await this.db.getFromIndex(
      'changeOrderContracts',
      'changeOrderId',
      changeOrderId
    );
  }

  async deleteChangeOrderContract(changeOrderContractId: number) {
    return await this.db.delete('changeOrderContracts', changeOrderContractId);
  }

  // Generated Change Order Contracts

  async addGeneratedChangeOrderContract(
    generatedChangeOrderContract: GeneratedChangeOrderContract,
    skipModified?: boolean
  ) {
    if (!skipModified && generatedChangeOrderContract.jobAddressId) {
      await this.setJobAddressAsModified(
        generatedChangeOrderContract.jobAddressId,
        'addGeneratedChangeOrderContract'
      );
    }
    return await this.db.add(
      'generatedChangeOrderContracts',
      generatedChangeOrderContract
    );
  }

  async getGeneratedChangeOrderContractById(changeOrderContractId: number) {
    return await this.db.getFromIndex(
      'generatedChangeOrderContracts',
      'changeOrderContractId',
      changeOrderContractId
    );
  }

  async getGeneratedChangeOrderContractByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'generatedChangeOrderContracts',
      'jobAddressId',
      jobAddressId
    );
  }

  async getGeneratedChangeOrderContractByChangeOrderContractId(
    changeOrderContractId: number
  ) {
    return await this.db.getFromIndex(
      'generatedChangeOrderContracts',
      'changeOrderContractId',
      changeOrderContractId
    );
  }

  async deleteGeneratedChangeOrderContract(
    generatedChangeOrderContractId: number
  ) {
    return await this.db.delete(
      'generatedChangeOrderContracts',
      generatedChangeOrderContractId
    );
  }

  // Calendar Notes

  async addCalendarNote(calendarNote: CalendarNote): Promise<number> {
    return await this.db.add('calendarNotes', calendarNote);
  }

  async getAllCalendarNotes(): Promise<CalendarNote[]> {
    return await this.db.getAll('calendarNotes');
  }

  async updateCalendarNote(calendarNote: CalendarNote): Promise<number> {
    return await this.db.put('calendarNotes', calendarNote);
  }

  async markCalendarNoteIsDeleted(calendarNote: CalendarNote): Promise<number> {
    calendarNote.isDeleted = true;
    return await this.db.put('calendarNotes', calendarNote);
  }

  async getAllModifiedCalendarNotes(): Promise<CalendarNote[]> {
    return await this.db.getAllFromIndex('calendarNotes', 'isModified', 1);
  }

  // Billing Statements

  async addBillingStatement(
    billingStatement: BillingStatement,
    skipModified?: boolean
  ): Promise<string> {
    if (!skipModified && billingStatement.jobAddressId) {
      await this.setJobAddressAsModified(
        billingStatement.jobAddressId,
        'addBillingStatement'
      );
    }
    return await this.db.put('billingStatements', billingStatement);
  }

  async getBillingStatementsByJobAddressId(
    jobAddressId: number
  ): Promise<BillingStatement[]> {
    return await this.db.getAllFromIndex(
      'billingStatements',
      'jobAddressId',
      jobAddressId
    );
  }

  async getBillingStatementByCustomerId(
    customerId: number
  ): Promise<BillingStatement | undefined> {
    return await this.db.getFromIndex(
      'billingStatements',
      'customerId',
      customerId
    );
  }

  async getBillingStatementsByFunctionId(functionId: string) {
    return await this.db.getAllFromIndex(
      'billingStatements',
      'functionId',
      functionId
    );
  }

  // Generated Invoices

  async addGeneratedInvoice(
    generatedInvoice: GeneratedInvoice,
    skipModified?: boolean
  ): Promise<string> {
    if (!skipModified && generatedInvoice.jobAddressId) {
      await this.setJobAddressAsModified(
        generatedInvoice.jobAddressId,
        'addGeneratedInvoice'
      );
    }
    return await this.db.put('generatedInvoices', generatedInvoice);
  }

  async getGeneratedInvoicesByJobAddressId(
    jobAddressId: number
  ): Promise<GeneratedInvoice[]> {
    return await this.db.getAllFromIndex(
      'generatedInvoices',
      'jobAddressId',
      jobAddressId
    );
  }

  async getGeneratedInvoiceByInvoiceNumber(invoiceNumber: string) {
    return await this.db.getFromIndex(
      'generatedInvoices',
      'invoiceNumber',
      invoiceNumber
    );
  }

  // Product Pricing

  async addProductPricing(productPricing: ProductPricing): Promise<number> {
    return await this.db.put('productPricing', productPricing);
  }

  async getProductPricingByZoneId(zoneId: number): Promise<ProductPricing[]> {
    return await this.db.getAllFromIndex('productPricing', 'zoneId', zoneId);
  }

  // Follow Ups

  async addFollowUp(
    followUp: FollowUp,
    skipModified?: boolean
  ): Promise<string> {
    if (!skipModified && followUp.jobAddressId) {
      await this.setJobAddressAsModified(followUp.jobAddressId, 'addFollowUp');
    }
    return await this.db.put('followUps', followUp);
  }

  async getFollowUpsByJobAddressId(jobAddressId: number): Promise<FollowUp[]> {
    return await this.db.getAllFromIndex(
      'followUps',
      'jobAddressId',
      jobAddressId
    );
  }

  async getFollowUpByCustomerVisitId(
    customerVisitId: string
  ): Promise<FollowUp | undefined> {
    return await this.db.getFromIndex(
      'followUps',
      'customerVisitId',
      customerVisitId
    );
  }

  async getFollowUpsByCustomerVisitId(
    customerVisitId: string
  ): Promise<FollowUp[]> {
    return await this.db.getAllFromIndex(
      'followUps',
      'customerVisitId',
      customerVisitId
    );
  }

  // Contact Methods

  async addContactMethod(
    contactMethod: ContactMethod,
    skipModified?: boolean
  ): Promise<number> {
    if (!skipModified && contactMethod.jobAddressId) {
      await this.setJobAddressAsModified(
        contactMethod.jobAddressId,
        'addContactMethod'
      );
    }
    return await this.db.add('contactMethods', contactMethod);
  }

  async getContactMethodsByCustomerId(
    customerId: number
  ): Promise<ContactMethod[]> {
    return await this.db.getAllFromIndex(
      'contactMethods',
      'customerId',
      customerId
    );
  }

  async getContactMethodsByJobAddressId(
    jobAddressId: number
  ): Promise<ContactMethod[]> {
    return await this.db.getAllFromIndex(
      'contactMethods',
      'jobAddressId',
      jobAddressId
    );
  }

  async updateContactMethod(
    contactMethod: ContactMethod,
    skipModified?: boolean
  ): Promise<number> {
    if (!skipModified && contactMethod.jobAddressId) {
      await this.setJobAddressAsModified(
        contactMethod.jobAddressId,
        'updateContactMethod'
      );
    }
    return await this.db.put('contactMethods', contactMethod);
  }

  // No Bid Reasons
  async addNoBidReason(noBidReason: NoBidReason): Promise<number> {
    return await this.db.add('noBidReasons', noBidReason);
  }

  async getAllNoBidReasons(): Promise<NoBidReason[]> {
    return await this.db.getAll('noBidReasons');
  }

  // Dispositions
  async addDisposition(disposition: Disposition): Promise<number> {
    return await this.db.add('dispositions', disposition);
  }

  async getAllDispositions(): Promise<Disposition[]> {
    return await this.db.getAll('dispositions');
  }

  // SubDispositions
  async addSubDisposition(subDisposition: SubDisposition): Promise<number> {
    return await this.db.add('subDispositions', subDisposition);
  }

  async getAllSubDispositions(): Promise<SubDisposition[]> {
    return await this.db.getAll('subDispositions');
  }

  // Mailing Requests
  async addMailingRequests(
    mailingRequests: MailingRequest,
    skipModified?: boolean
  ): Promise<number> {
    if (!skipModified && mailingRequests.jobAddressId) {
      await this.setJobAddressAsModified(
        mailingRequests.jobAddressId,
        'addMailingRequests'
      );
    }
    return await this.db.put('mailingRequests', mailingRequests);
  }

  async getMailingRequestsByJobAddressId(
    jobAddressId: number
  ): Promise<MailingRequest[]> {
    return await this.db.getAllFromIndex(
      'mailingRequests',
      'jobAddressId',
      jobAddressId
    );
  }

  // Drawings
  async addDrawing(drawing: Drawing, skipModified?: boolean): Promise<string> {
    if (!skipModified && drawing.jobAddressId) {
      await this.setJobAddressAsModified(drawing.jobAddressId, 'addDrawing');
    }
    return await this.db.put('drawings', drawing);
  }

  async getDrawingsByJobAddressId(
    jobAddressId: number,
    includeDeletedDrawings: boolean
  ): Promise<Drawing[]> {
    const tx = this.db.transaction('drawings', 'readonly');
    const store = tx.objectStore('drawings');
    const index = store.index('jobAddressId');

    const drawings = await index.getAll(jobAddressId);
    const notDeletedDrawings: Drawing[] = [];

    drawings.forEach(async (drawing) => {
      if (!drawing.isDeleted || includeDeletedDrawings) {
        notDeletedDrawings.push(drawing);
      }
    });
    return notDeletedDrawings;
  }

  deleteDrawing(drawingId: string): Promise<void> {
    return this.db.delete('drawings', drawingId);
  }

  // Prod Dailies
  async addProdDaily(prodDaily: ProdDaily, skipModified?: boolean) {
    if (!skipModified && prodDaily.jobAddressId) {
      await this.setJobAddressAsModified(
        prodDaily.jobAddressId,
        'addProdDaily'
      );
    }
    return await this.db.put('prodDailies', prodDaily);
  }

  async getProdDailiesByContractId(contractId: number) {
    return await this.db.getAllFromIndex(
      'prodDailies',
      'contractId',
      contractId
    );
  }

  async getProdDailiesByProductionId(productionId: string) {
    return await this.db.getAllFromIndex(
      'prodDailies',
      'productionId',
      productionId
    );
  }

  async getProdDailiesByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'prodDailies',
      'jobAddressId',
      jobAddressId
    );
  }

  async checkForIncompleteProdDailies() {
    const incompleteProdDailies: ProdDaily[] = [];

    const currentDate = new Date();

    const yesterdayDate = currentDate.setDate(currentDate.getDate() - 1);
    const yesterdayDateString = formatDate(
      yesterdayDate,
      'yyyy-MM-dd',
      'en-US'
    );

    const tx = this.db.transaction('prodDailies', 'readonly');
    const store = tx.objectStore('prodDailies');
    const index = store.index('dailyDate');

    const allPreviousDailies = await index.getAll(
      IDBKeyRange.upperBound(yesterdayDateString)
    );
    const userName = localStorage.getItem('name');
    for (const daily of allPreviousDailies) {
      // Only prompt for dailies that are created by the current user.
      if (!daily.isComplete && daily.createdBy == userName) {
        const tx2 = this.db.transaction('productionVisits', 'readonly');
        const store2 = tx2.objectStore('productionVisits');
        const index2 = store2.index('productionId');

        const productionVisit = await index2.get(daily.productionId);
        // Only include the daily if it's status is 0, 1, or 5
        if (
          (productionVisit && productionVisit.status === 0) ||
          (productionVisit && productionVisit.status === 1) ||
          (productionVisit && productionVisit.status === 5)
        ) {
          incompleteProdDailies.push(daily);
        }
      }
    }
    return incompleteProdDailies;
  }

  async checkForProdAppointmentsNeedingDailies() {
    const prodAppointmentsNeedingDaily: ProdAppointment[] = [];

    const currentDate = new Date();
    const formattedCurrentDate = currentDate.toISOString().split('T')[0];

    // Use startDate to only require dailies for appointments
    // that are scheduled after this update is released.
    const startDate = new Date('2024-09-21').toISOString().split('T')[0];

    const tx = this.db.transaction('prodAppointments', 'readonly');
    const store = tx.objectStore('prodAppointments');
    const index = store.index('effectiveScheduledDate');

    const allPastProdAppointments = await index.getAll(
      IDBKeyRange.bound(startDate, formattedCurrentDate, false, true)
    );

    for (const appointment of allPastProdAppointments) {
      const tx2 = this.db.transaction('prodDailies', 'readonly');
      const store2 = tx2.objectStore('prodDailies');
      const index2 = store2.index('dailyDate');

      // effectiveScheduledDate is already 'YYYY-MM-DD'
      const appointmentDateString =
        appointment.effectiveScheduledDate ?? appointment.scheduledDate;

      const dailiesOnAppointmentDate = await index2.getAll(
        IDBKeyRange.only(appointmentDateString)
      );

      if (dailiesOnAppointmentDate.length === 0) {
        prodAppointmentsNeedingDaily.push(appointment);
      } else {
        // find any daily that has the same jobAddressId and contractId as the appointment
        const matchingDaily = dailiesOnAppointmentDate.find(
          (daily) =>
            daily.jobAddressId === appointment.jobAddressId &&
            daily.contractId === appointment.contractId
        );

        if (!matchingDaily) {
          prodAppointmentsNeedingDaily.push(appointment);
        }
      }
    }

    // filter prodAppointmentsNeedingDaily to only include appointments with a unique combination of
    // jobAddressId, effectiveScheduledDate, and contractId
    const uniqueProdAppointmentsNeedingDaily =
      prodAppointmentsNeedingDaily.filter(
        (appointment, index, self) =>
          index ===
          self.findIndex(
            (t) =>
              t.jobAddressId === appointment.jobAddressId &&
              (t.effectiveScheduledDate ?? t.scheduledDate) ===
                (appointment.effectiveScheduledDate ??
                  appointment.scheduledDate) &&
              t.contractId === appointment.contractId
          )
      );

    const finalProdAppointmentsNeedingDaily: ProdAppointment[] = [];

    // Exclude appointments with finished production visits from the list
    for (const appointment of uniqueProdAppointmentsNeedingDaily) {
      const tx3 = this.db.transaction('productionVisits', 'readonly');
      const store3 = tx3.objectStore('productionVisits');
      const index3 = store3.index('contractId');

      const allProductionVisits = await index3.getAll(appointment.contractId);
      const userName = localStorage.getItem('name');

      // Filter out productionVisits that were created by a different user.
      const productionVisitsCreatedByCurrentUser = allProductionVisits.filter(
        (visit) => visit.createdBy == userName || visit.createdBy == 'System'
      );

      if (productionVisitsCreatedByCurrentUser.length > 0) {
        productionVisitsCreatedByCurrentUser.forEach((visit) => {
          if (visit.status === 0 || visit.status === 5) {
            finalProdAppointmentsNeedingDaily.push(appointment);
          }
        });
      } else {
        finalProdAppointmentsNeedingDaily.push(appointment);
      }
    }

    return finalProdAppointmentsNeedingDaily;
  }

  // async checkForProdAppointmentsNeedingDailies() {
  //   const prodAppointmentsNeedingDaily: ProdAppointment[] = [];

  //   const currentDate = new Date();
  //   const formattedCurrentDate = currentDate.toISOString().split('T')[0];

  //   const tx = this.db.transaction('prodAppointments', 'readonly');
  //   const store = tx.objectStore('prodAppointments');
  //   const index = store.index('scheduledDate');

  //   // Use startDate to only require dailies for appointments
  //   // that are scheduled after this update is released.
  //   const startDate = new Date('2024-09-21').toISOString().split('T')[0];

  //   const allPastProdAppointments = await index.getAll(
  //     IDBKeyRange.bound(startDate, formattedCurrentDate)
  //   );

  //   for (const appointment of allPastProdAppointments) {
  //     const tx2 = this.db.transaction('prodDailies', 'readonly');
  //     const store2 = tx2.objectStore('prodDailies');
  //     const index2 = store2.index('dailyDate');

  //     const appointmentDateString = formatDate(
  //       appointment.scheduledDate,
  //       'yyyy-MM-dd',
  //       'en-US'
  //     );

  //     const dailiesOnAppointmentDate = await index2.getAll(
  //       IDBKeyRange.only(appointmentDateString)
  //     );

  //     if (dailiesOnAppointmentDate.length === 0) {
  //       prodAppointmentsNeedingDaily.push(appointment);
  //     } else {
  //       // find any daily that has the same jobAddressId and contractId as the appointment
  //       // If none are found, then add the appointment to the list
  //       let matchingDaily = dailiesOnAppointmentDate.find((daily) => {
  //         if (
  //           daily.jobAddressId === appointment.jobAddressId &&
  //           daily.contractId === appointment.contractId
  //         ) {
  //           return daily;
  //         } else {
  //           return;
  //         }
  //       });

  //       if (!matchingDaily) {
  //         prodAppointmentsNeedingDaily.push(appointment);
  //       }
  //     }
  //   }

  //   // console.log('prodAppointmentsNeedingDaily', prodAppointmentsNeedingDaily);

  //   // filter prodAppointmentsNeedingDaily to only include appointments with a unique combination of jobAddressId, scheduledDate, and contractId
  //   const uniqueProdAppointmentsNeedingDaily =
  //     prodAppointmentsNeedingDaily.filter(
  //       (appointment, index, self) =>
  //         index ===
  //         self.findIndex(
  //           (t) =>
  //             t.jobAddressId === appointment.jobAddressId &&
  //             t.scheduledDate === appointment.scheduledDate &&
  //             t.contractId === appointment.contractId
  //         )
  //     );

  //   const finalProdAppointmentsNeedingDaily: ProdAppointment[] = [];

  //   // console.log(
  //   //   'uniqueProdAppointmentsNeedingDaily',
  //   //   uniqueProdAppointmentsNeedingDaily
  //   // );

  //   // Exclude appointments with finished production visits from the list
  //   for (const appointment of uniqueProdAppointmentsNeedingDaily) {
  //     const tx3 = this.db.transaction('productionVisits', 'readonly');
  //     const store3 = tx3.objectStore('productionVisits');
  //     const index3 = store3.index('contractId');

  //     const allProductionVisits = await index3.getAll(appointment.contractId);
  //     const userName = localStorage.getItem('name');
  //     // Filter out productionVisits that were created by a different user.
  //     const productionVisitsCreatedByCurrentUser = allProductionVisits.filter(
  //       (visit) => visit.createdBy == userName || visit.createdBy == 'System'
  //     );

  //     // console.log('productionVisitsCreatedByCurrentUser', productionVisitsCreatedByCurrentUser);

  //     if (productionVisitsCreatedByCurrentUser.length > 0) {
  //       productionVisitsCreatedByCurrentUser.forEach((visit) => {
  //         if (visit.status === 0 || visit.status === 5) {
  //           finalProdAppointmentsNeedingDaily.push(appointment);
  //         }
  //       });
  //     } else {
  //       finalProdAppointmentsNeedingDaily.push(appointment);
  //     }

  //     // console.log('finalProdAppointmentsNeedingDaily', finalProdAppointmentsNeedingDaily);
  //   }
  //   return finalProdAppointmentsNeedingDaily;
  // }

  // Time Logs

  async addTimeLog(timeLog: TimeLog, skipModified?: boolean) {
    if (!skipModified && timeLog.jobAddressId) {
      await this.setJobAddressAsModified(timeLog.jobAddressId, 'addTimeLog');
    }
    return await this.db.put('timeLogs', timeLog);
  }

  async getTimeLogsByProdDailyId(prodDailyId: string) {
    return await this.db.getAllFromIndex(
      'timeLogs',
      'prodDailyId',
      prodDailyId
    );
  }

  async getTimeLogsByProductionId(productionId: string) {
    return await this.db.getAllFromIndex(
      'timeLogs',
      'productionId',
      productionId
    );
  }

  async getTimeLogsByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'timeLogs',
      'jobAddressId',
      jobAddressId
    );
  }

  async deleteTimeLog(timeLogId: string) {
    return await this.db.delete('timeLogs', timeLogId);
  }

  // Time BreakDown
  async addTimeBreakDown(timeBreakDown: TimeBreakDown, skipModified?: boolean) {
    if (!skipModified && timeBreakDown.jobAddressId) {
      await this.setJobAddressAsModified(
        timeBreakDown.jobAddressId,
        'addTimeBreakDown'
      );
    }
    return await this.db.put('timeBreakDowns', timeBreakDown);
  }

  async getTimeBreakDownByProdDailyId(prodDailyId: string) {
    return await this.db.getFromIndex(
      'timeBreakDowns',
      'prodDailyId',
      prodDailyId
    );
  }

  async getTimeBreakDownsByProductionId(productionId: string) {
    return await this.db.getAllFromIndex(
      'timeBreakDowns',
      'productionId',
      productionId
    );
  }

  async getTimeBreakDownsByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'timeBreakDowns',
      'jobAddressId',
      jobAddressId
    );
  }

  async deleteTimeBreakDown(timeBreakDownId: string) {
    return await this.db.delete('timeBreakDowns', timeBreakDownId);
  }

  // Crew Members
  async addSpectrumCrewMember(crewMember: SpectrumCrewMember) {
    return await this.db.add('spectrumCrewMembers', crewMember);
  }

  async getAllSpectrumCrewMembers() {
    return await this.db.getAll('spectrumCrewMembers');
  }

  // Crews
  async addCrew(crew: Crew, skipModified?: boolean) {
    if (!skipModified && crew.jobAddressId) {
      await this.setJobAddressAsModified(crew.jobAddressId, 'addCrew');
    }
    return await this.db.put('crews', crew);
  }

  async getCrewByProdDailyId(prodDailyId: string) {
    return await this.db.getFromIndex('crews', 'prodDailyId', prodDailyId);
  }

  async getCrewByProductionId(productionId: string) {
    return await this.db.getFromIndex('crews', 'productionId', productionId);
  }

  async getCrewsByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex('crews', 'jobAddressId', jobAddressId);
  }

  // Materials
  async addSpectrumMaterial(spectrumMaterial: SpectrumMaterial) {
    return await this.db.add('spectrumMaterials', spectrumMaterial);
  }

  async getAllSpectrumMaterials() {
    return await this.db.getAll('spectrumMaterials');
  }

  // Daily Materials
  async addDailyMaterial(dailyMaterial: DailyMaterial, skipModified?: boolean) {
    if (!skipModified && dailyMaterial.jobAddressId) {
      await this.setJobAddressAsModified(
        dailyMaterial.jobAddressId,
        'addDailyMaterial'
      );
    }
    return await this.db.put('dailyMaterials', dailyMaterial);
  }

  async getDailyMaterialsByProdDailyId(prodDailyId: string) {
    return await this.db.getAllFromIndex(
      'dailyMaterials',
      'prodDailyId',
      prodDailyId
    );
  }

  async getDailyMaterialsByProductionId(productionId: string) {
    return await this.db.getAllFromIndex(
      'dailyMaterials',
      'productionId',
      productionId
    );
  }

  async getDailyMaterialsByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'dailyMaterials',
      'jobAddressId',
      jobAddressId
    );
  }

  async deleteDailyMaterial(dailyMaterialId: string) {
    return await this.db.delete('dailyMaterials', dailyMaterialId);
  }

  // Daily Payment Info

  async addDailyPaymentInfo(
    dailyPaymentInfo: DailyPaymentInfo,
    skipModified?: boolean
  ) {
    if (!skipModified && dailyPaymentInfo.jobAddressId) {
      await this.setJobAddressAsModified(
        dailyPaymentInfo.jobAddressId,
        'addDailyPaymentInfo'
      );
    }
    return await this.db.put('dailyPaymentInfo', dailyPaymentInfo);
  }

  async getDailyPaymentInfoByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'dailyPaymentInfo',
      'jobAddressId',
      jobAddressId
    );
  }

  async getDailyPaymentInfoByProductionId(productionId: string) {
    return await this.db.getFromIndex(
      'dailyPaymentInfo',
      'productionId',
      productionId
    );
  }

  // Daily Prompts

  async addDailyPrompt(dailyPrompt: DailyPrompt, skipModified?: boolean) {
    if (!skipModified && dailyPrompt.jobAddressId) {
      await this.setJobAddressAsModified(
        dailyPrompt.jobAddressId,
        'addDailyPrompt'
      );
    }
    return await this.db.put('dailyPrompts', dailyPrompt);
  }

  async getDailyPromptsByProdDailyId(prodDailyId: string) {
    return await this.db.getAllFromIndex(
      'dailyPrompts',
      'prodDailyId',
      prodDailyId
    );
  }

  async getDailyPromptsByJobAddressId(jobAddressId: number) {
    return await this.db.getAllFromIndex(
      'dailyPrompts',
      'jobAddressId',
      jobAddressId
    );
  }

  async getDailyPromptsByProductionId(productionId: string) {
    return await this.db.getAllFromIndex(
      'dailyPrompts',
      'productionId',
      productionId
    );
  }

  // Vehicle Documentation Attachments

  async addVehicleDocumentationAttachment(
    vehicleDocumentationAttachment: VehicleDocumentationAttachment
  ) {
    return await this.db.add(
      'vehicleDocumentationAttachments',
      vehicleDocumentationAttachment
    );
  }

  async getAllVehicleDocumentationAttachments() {
    return await this.db.getAll('vehicleDocumentationAttachments');
  }

  constructor() {}
}

export const idb = new AccutraxIDB();
